<template>
  <div
    class="employee-column relative"
    :data-wc-column-id="String(col.id)"
    :class="{
      'helper-column-ma': col.id === 'ma_info',
      'helper-column-no-show': col.id === 'no_show',
      'helper-column-unassigned': col.id === 'unassigned',
      'wc-thick-lines': isThickerLines,
      'wc-col-drag-valid': isHovered && hoverState && !hoverState.error,
      'wc-col-drag-invalid': isHovered && hoverState && hoverState.error,
      'sick-employee-column': isFullySick,
      'wc-pointer-drag-fallback': usesPointerDragFallback,
      'wc-touch-interactions': hasTouchHardware,
    }"
    :style="columnStyle"
    @dragover.prevent="onDragOver"
    @drop="onDrop"
    @dragleave="$emit('drag-leave')">
    <!-- Sticky Header with Hover Info Menu for Trainers (only activates when hovering the avatar) -->
    <div
      v-if="!col.isHelper && col.hoverDetails"
      class="column-header wc-col-header"
      :class="{ 'cursor-pointer hover-opacity': $vuetify.breakpoint.smAndDown }"
      @click="$emit('click-header', col)">
      <!-- Left side: Avatar -->
      <div class="wc-col-header-left">
        <v-menu
          open-on-hover
          bottom
          offset-y
          :close-delay="150"
          :open-delay="250"
          nudge-bottom="4"
          content-class="wc-trainer-hover-card-menu"
          :disabled="isDraggingOrResizing || !hasHoverSupport">
          <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
            <span v-bind="menuAttrs" v-on="menuOn" style="cursor: pointer; display: inline-block">
              <v-badge
                bottom
                overlap
                :color="col.liveStatus ? col.liveStatus.color : 'transparent'"
                dot
                offset-x="10"
                offset-y="10"
                :value="!!col.liveStatus">
                <v-avatar size="40" class="wc-col-avatar">
                  <img v-if="col.picture" :src="'/img/profilbilder/' + col.picture" style="object-fit: cover" />
                  <img v-else src="/img/default-avatar.png" style="object-fit: cover" />
                </v-avatar>
              </v-badge>
            </span>
          </template>

          <!-- Hover Card / Popover Content -->
          <v-card
            :dark="isDarkMode"
            class="wc-trainer-hover-card pa-3"
            outlined
            :style="{
              borderRadius: '8px',
              minWidth: '260px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
              border: '1px solid ' + (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'),
              background: (isDarkMode ? '#1e1e1e' : '#ffffff') + ' !important',
            }">
            <div class="d-flex align-center mb-2 pb-2" style="border-bottom: 1px solid var(--border-light)">
              <v-avatar size="36" class="mr-2">
                <img v-if="col.picture" :src="'/img/profilbilder/' + col.picture" style="object-fit: cover" />
                <img v-else src="/img/default-avatar.png" style="object-fit: cover" />
              </v-avatar>
              <div>
                <div style="font-weight: 700; font-size: 0.95rem; color: var(--text-main)">
                  {{ col.firstName }} {{ col.info ? col.info.lastName : "" }}
                </div>
                <div style="font-size: 0.75rem; color: var(--text-muted)">
                  {{ col.shiftRange ? "Schicht: " + col.shiftRange : "Keine Schicht heute" }}
                </div>
              </div>
            </div>

            <div class="d-flex flex-column" style="gap: 8px">
              <!-- 1. Next shifts -->
              <div
                class="d-flex flex-column"
                style="font-size: 0.85rem; gap: 4px; border-bottom: 1px solid var(--border-light); padding-bottom: 8px; margin-bottom: 4px">
                <div class="d-flex align-center" style="font-weight: 700; color: var(--text-main); margin-bottom: 2px">
                  <v-icon small class="mr-2" color="primary">mdi-calendar-clock</v-icon>
                  Nächste Schichten:
                </div>
                <div
                  v-for="(shift, sIdx) in col.hoverDetails.nextShifts"
                  :key="'sh-' + sIdx"
                  class="pl-6 d-flex align-center"
                  style="color: var(--text-muted); font-size: 0.8rem; line-height: 1.2; gap: 6px">
                  <span style="font-weight: 600; color: var(--text-main)">{{ shift.date }}</span>
                  <span>{{ shift.time }}</span>
                </div>
              </div>

              <!-- 2. Tasks -->
              <div
                class="d-flex flex-column"
                style="font-size: 0.85rem; gap: 4px; border-bottom: 1px solid var(--border-light); padding-bottom: 8px; margin-bottom: 4px">
                <div class="d-flex align-center" style="font-weight: 700; color: var(--text-main)">
                  <v-icon small class="mr-2" :color="col.hoverDetails.remainingTasks > 0 ? 'warning' : 'success'">
                    {{ col.hoverDetails.remainingTasks > 0 ? "mdi-checkbox-blank-outline" : "mdi-checkbox-marked-circle-outline" }}
                  </v-icon>
                  <span>{{ col.hoverDetails.taskText }}</span>
                </div>
                <div
                  v-for="(task, tIdx) in col.hoverDetails.openTasks"
                  :key="'t-' + tIdx"
                  class="pl-6"
                  style="color: var(--text-muted); font-size: 0.8rem; line-height: 1.2">
                  • {{ task }}
                </div>
              </div>

              <!-- 3. Holidays -->
              <div class="d-flex align-center" style="font-size: 0.85rem">
                <v-icon small class="mr-2" :color="col.hoverDetails.isCurrentlyOnHoliday ? 'orange' : 'teal'">
                  {{ col.hoverDetails.isCurrentlyOnHoliday ? "mdi-alert-circle-outline" : "mdi-calendar-range" }}
                </v-icon>
                <span :style="col.hoverDetails.isCurrentlyOnHoliday ? 'color: var(--text-main); font-weight: 700;' : 'color: var(--text-main)'">
                  {{ col.hoverDetails.holidayText }}
                </span>
              </div>
            </div>
          </v-card>
        </v-menu>
      </div>

      <!-- Center side: Info & Badges in vertical stack -->
      <div class="wc-col-header-info">
        <div class="wc-col-name">{{ formatTrainerName(col) }}</div>

        <div class="wc-col-badges-row">
          <!-- Main Utilization Badge (with Hover Tooltip) -->
          <v-tooltip bottom v-if="col.backgrounds && col.backgrounds.length > 0">
            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
              <div
                v-bind="tooltipAttrs"
                v-on="tooltipOn"
                class="wc-col-badge px-1"
                :style="{
                  background: utilizationColor + '15',
                  border: '1px solid ' + utilizationColor + '40',
                  color: utilizationColor,
                  boxShadow: '0 2px 8px ' + utilizationColor + '20',
                }">
                <v-icon x-small :color="utilizationColor" style="font-size: 10px; margin-right: 2px">mdi-flash</v-icon>
                <span style="font-weight: 800; font-size: 0.65rem">{{ utilizationPercentage }}%</span>
              </div>
            </template>
            <span>Auslastung: {{ utilizationPercentage }}% ({{ appointmentCount }} Termine / {{ taskCount }} Aufgaben)</span>
          </v-tooltip>

          <!-- Note Badge -->
          <div
            v-if="notesCount > 0"
            class="wc-col-badge wc-col-badge-note"
            @click.stop="$emit('open-note', col)"
            style="cursor: pointer"
            title="Notizen ansehen">
            <v-icon x-small style="font-size: 10px; margin-right: 2px">mdi-message-text</v-icon>
            <span style="font-size: 0.65rem">{{ notesCount }}</span>
          </div>
        </div>
      </div>

      <!-- Right side: Menu -->
      <div class="wc-col-header-right">
        <v-menu offset-y left>
          <template v-slot:activator="{ on: dotsOn, attrs: dotsAttrs }">
            <v-btn icon small v-bind="dotsAttrs" v-on="dotsOn" @click.stop :style="isDarkMode ? 'color: rgba(255, 255, 255, 0.3)' : 'color: black'">
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense dark class="rounded-lg" style="background: #1a1a1a">
            <v-list-item @click="$emit('open-note', col)">
              <v-icon small left color="blue lighten-2">mdi-message-text</v-icon>
              <v-list-item-title>Notiz senden</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('set-focus', col)">
              <v-icon small left :color="isFocused ? 'amber' : 'amber lighten-2'">
                {{ isFocused ? "mdi-account-star" : "mdi-account-star-outline" }}
              </v-icon>
              <v-list-item-title>{{ isFocused ? "Fokus aufheben" : "Fokus setzen" }}</v-list-item-title>
            </v-list-item>
            <v-divider style="border-color: rgba(255, 255, 255, 0.1)"></v-divider>
            <v-list-item @click="$emit('edit-shift', col)">
              <v-icon small left color="orange lighten-2">mdi-clock-edit-outline</v-icon>
              <v-list-item-title>Schichtzeiten anpassen</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('toggle-sick', col)">
              <v-icon small left :color="isSick ? 'green lighten-2' : 'red lighten-2'">
                {{ isSick ? "mdi-emoticon-happy-outline" : "mdi-emoticon-sick-outline" }}
              </v-icon>
              <v-list-item-title>
                {{
                  isSick
                    ? col.absenceReason && col.absenceReason !== "sick"
                      ? "Als anwesend markieren"
                      : "Als gesund markieren"
                    : "Als krank markieren"
                }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('transfer-shift', col)">
              <v-icon small left color="purple lighten-2">mdi-account-switch</v-icon>
              <v-list-item-title>Schicht übertragen</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('swap-shift', col)">
              <v-icon small left color="indigo lighten-2">mdi-swap-horizontal</v-icon>
              <v-list-item-title>Schicht tauschen</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Fallback header when no hover menu is applicable (e.g. Helper column) -->
    <div
      v-else
      class="column-header wc-col-header"
      :class="{ 'cursor-pointer hover-opacity': $vuetify.breakpoint.smAndDown }"
      @click="$emit('click-header', col)">
      <!-- Left side: Avatar -->
      <div class="wc-col-header-left">
        <v-badge
          bottom
          overlap
          :color="col.liveStatus ? col.liveStatus.color : 'transparent'"
          dot
          offset-x="10"
          offset-y="10"
          :value="!!col.liveStatus">
          <v-avatar size="40" class="wc-col-avatar" :style="col.isHelper ? 'background: var(--bg-panel)' : ''">
            <img v-if="col.picture" :src="'/img/profilbilder/' + col.picture" style="object-fit: cover" />
            <v-icon v-else-if="col.isHelper" :color="isDarkMode ? 'white' : 'grey darken-3'">{{ col.info.icon }}</v-icon>
            <img v-else src="/img/default-avatar.png" style="object-fit: cover" />
          </v-avatar>
        </v-badge>
      </div>

      <!-- Center side: Info & Badges in vertical stack -->
      <div class="wc-col-header-info">
        <div class="wc-col-name">{{ col.firstName }}</div>

        <div class="wc-col-badges-row" v-if="!col.isHelper">
          <!-- Main Utilization Badge (with Hover Tooltip) -->
          <v-tooltip bottom v-if="col.backgrounds && col.backgrounds.length > 0">
            <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
              <div
                v-bind="tooltipAttrs"
                v-on="tooltipOn"
                class="wc-col-badge px-1"
                :style="{
                  background: utilizationColor + '15',
                  border: '1px solid ' + utilizationColor + '40',
                  color: utilizationColor,
                  boxShadow: '0 2px 8px ' + utilizationColor + '20',
                }">
                <v-icon x-small :color="utilizationColor" style="font-size: 10px; margin-right: 2px">mdi-flash</v-icon>
                <span style="font-weight: 800; font-size: 0.65rem">{{ utilizationPercentage }}%</span>
              </div>
            </template>
            <span>Auslastung: {{ utilizationPercentage }}% ({{ appointmentCount }} Termine / {{ taskCount }} Aufgaben)</span>
          </v-tooltip>

          <!-- Note Badge -->
          <div
            v-if="notesCount > 0"
            class="wc-col-badge wc-col-badge-note"
            @click.stop="$emit('open-note', col)"
            style="cursor: pointer"
            title="Notizen ansehen">
            <v-icon x-small style="font-size: 10px; margin-right: 2px">mdi-message-text</v-icon>
            <span style="font-size: 0.65rem">{{ notesCount }}</span>
          </div>
        </div>

        <!-- Fallback badges for Helper Columns if isHelper -->
        <div class="wc-col-badges-row" v-else-if="col.isHelper">
          <div v-if="appointmentCount > 0" class="wc-col-badge">
            <v-icon x-small style="font-size: 10px">mdi-calendar</v-icon>
            {{ appointmentCount }}
          </div>
          <div v-if="taskCount > 0" class="wc-col-badge wc-col-badge-task">
            <v-icon x-small style="font-size: 10px">mdi-checkbox-marked</v-icon>
            {{ taskCount }}
          </div>
          <div
            v-if="notesCount > 0"
            class="wc-col-badge wc-col-badge-note"
            @click.stop="$emit('open-note', col)"
            style="cursor: pointer"
            title="Notizen ansehen">
            <v-icon x-small style="font-size: 10px; margin-right: 2px">mdi-message-text</v-icon>
            <span style="font-size: 0.65rem">{{ notesCount }}</span>
          </div>
        </div>
      </div>

      <!-- Right side: Menu -->
      <div class="wc-col-header-right" v-if="!col.isHelper">
        <v-menu offset-y left>
          <template v-slot:activator="{ on: dotsOn, attrs: dotsAttrs }">
            <v-btn icon small v-bind="dotsAttrs" v-on="dotsOn" @click.stop :style="isDarkMode ? 'color: rgba(255, 255, 255, 0.3)' : 'color: black'">
              <v-icon small>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense dark class="rounded-lg" style="background: #1a1a1a">
            <v-list-item @click="$emit('open-note', col)">
              <v-icon small left color="blue lighten-2">mdi-message-text</v-icon>
              <v-list-item-title>Notiz senden</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('set-focus', col)">
              <v-icon small left :color="isFocused ? 'amber' : 'amber lighten-2'">
                {{ isFocused ? "mdi-account-star" : "mdi-account-star-outline" }}
              </v-icon>
              <v-list-item-title>{{ isFocused ? "Fokus aufheben" : "Fokus setzen" }}</v-list-item-title>
            </v-list-item>
            <v-divider style="border-color: rgba(255, 255, 255, 0.1)"></v-divider>
            <v-list-item @click="$emit('edit-shift', col)">
              <v-icon small left color="orange lighten-2">mdi-clock-edit-outline</v-icon>
              <v-list-item-title>Schichtzeiten anpassen</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('toggle-sick', col)">
              <v-icon small left :color="isSick ? 'green lighten-2' : 'red lighten-2'">
                {{ isSick ? "mdi-emoticon-happy-outline" : "mdi-emoticon-sick-outline" }}
              </v-icon>
              <v-list-item-title>
                {{
                  isSick
                    ? col.absenceReason && col.absenceReason !== "sick"
                      ? "Als anwesend markieren"
                      : "Als gesund markieren"
                    : "Als krank markieren"
                }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('transfer-shift', col)">
              <v-icon small left color="purple lighten-2">mdi-account-switch</v-icon>
              <v-list-item-title>Schicht übertragen</v-list-item-title>
            </v-list-item>
            <v-list-item @click="$emit('swap-shift', col)">
              <v-icon small left color="indigo lighten-2">mdi-swap-horizontal</v-icon>
              <v-list-item-title>Schicht tauschen</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Lane Body -->
    <div class="lane-body relative" data-wc-lane :style="{ height: hoursCount * hourHeight + 'px' }" ref="laneBody">
      <!-- Abwesenheit bzw. bewusst bestätigter Ferieneinsatz -->
      <div v-if="isFullySick && !col.isHelper" class="sick-warning-banner" :style="getSickWarningBannerStyle(isDarkMode)">
        <div class="d-flex align-center justify-center flex-wrap" style="gap: 4px">
          <v-icon
            x-small
            :color="isDarkMode ? getAbsenceConfig(col.absenceReason).color : getAbsenceConfig(col.absenceReason).lightColor"
            class="mr-0.5">
            {{ getAbsenceConfig(col.absenceReason).icon }}
          </v-icon>
          <span
            class="sick-warning-text"
            :style="{ color: isDarkMode ? getAbsenceConfig(col.absenceReason).color : getAbsenceConfig(col.absenceReason).lightColor }">
            {{ getAbsenceConfig(col.absenceReason).bannerText }}
          </span>
        </div>
        <v-btn
          v-if="col.absenceReason === 'sick' || col.absenceReason === 'Krankheit'"
          x-small
          outlined
          :color="isDarkMode ? getAbsenceConfig(col.absenceReason).color : getAbsenceConfig(col.absenceReason).lightColor"
          class="mt-1 px-2 py-0.5 rounded-lg"
          style="height: auto; min-width: auto; text-transform: none; font-size: 0.7rem"
          :style="{
            borderColor: isDarkMode ? getAbsenceConfig(col.absenceReason).color + '66' : getAbsenceConfig(col.absenceReason).lightColor + '66',
          }"
          @click="$emit('hide-sick-column', col.id)">
          Ausblenden
        </v-btn>
      </div>
      <div
        v-else-if="col.worksDuringHoliday && !col.isHelper"
        class="sick-warning-banner holiday-work-warning-banner"
        :style="getSickWarningBannerStyle(isDarkMode)">
        <v-icon x-small :color="isDarkMode ? getAbsenceConfig('holiday').color : getAbsenceConfig('holiday').lightColor" class="mr-1">
          {{ getAbsenceConfig("holiday").icon }}
        </v-icon>
        <span class="sick-warning-text" :style="{ color: isDarkMode ? getAbsenceConfig('holiday').color : getAbsenceConfig('holiday').lightColor }">
          Ferien – Einsatz bewusst bestätigt
        </span>
      </div>
      <!-- Sick Employees Banner/Cards -->
      <div v-if="col.id === 'ma_info' && sickEmployeesToday && sickEmployeesToday.length > 0" class="sick-employees-container pa-2">
        <!-- Toggle Button for Collapsing -->
        <div class="d-flex justify-center mb-1 w-100">
          <v-btn
            x-small
            outlined
            color="orange lighten-1"
            class="font-weight-bold text-none rounded-lg white--text px-1"
            style="
              background: #2e2e2e;
              border: 1px solid rgba(255, 152, 0, 0.4);
              height: 20px;
              width: 100%;
              max-width: 135px;
              font-size: 0.63rem;
              pointer-events: auto;
              z-index: 95;
            "
            @click="isAbsencesCollapsed = !isAbsencesCollapsed">
            <v-icon left x-small style="margin-right: 2px">{{ isAbsencesCollapsed ? "mdi-chevron-down" : "mdi-chevron-up" }}</v-icon>
            Abwesenheiten ({{ sickEmployeesToday.length }})
          </v-btn>
        </div>

        <div v-if="!isAbsencesCollapsed">
          <v-card
            v-for="emp in sickEmployeesToday"
            :key="emp.user"
            class="sick-emp-card mb-2 rounded-lg elevation-4 border-error-light"
            :style="getSickEmpCardStyle(emp)">
            <div class="sick-emp-card-header pa-3 pb-2" :style="getAbsenceHeaderStyle(emp.absenceReason)">
              <div class="sick-emp-card-identity">
                <v-avatar size="36" class="sick-emp-card-avatar" :style="{ border: '2px solid ' + getAbsenceConfig(emp.absenceReason).color }">
                  <img v-if="emp.picture" :src="'/img/profilbilder/' + emp.picture" style="object-fit: cover" />
                  <v-icon v-else :color="getAbsenceConfig(emp.absenceReason).color" small>mdi-account-alert</v-icon>
                </v-avatar>
                <div class="sick-emp-card-copy text-left">
                  <div class="sick-emp-card-name">
                    {{ emp.fullName }}
                  </div>
                  <div
                    class="sick-emp-card-label font-weight-black text-overline"
                    :style="{
                      color: isDarkMode ? getAbsenceConfig(emp.absenceReason).color : getAbsenceConfig(emp.absenceReason).lightColor,
                    }">
                    {{ emp.worksDuringHoliday ? "FERIEN · EINSATZ BESTÄTIGT" : getAbsenceConfig(emp.absenceReason).cardLabel
                    }}{{ emp.sickPercentage !== undefined && emp.sickPercentage < 100 ? ` (${emp.sickPercentage}%)` : "" }}
                  </div>
                </div>
              </div>
              <!-- Toggle show/hide button -->
              <div class="sick-emp-card-action" v-if="emp.absenceReason === 'sick' || emp.absenceReason === 'Krankheit'">
                <v-btn
                  v-if="hiddenSickTrainerIds.includes(String(emp.user))"
                  x-small
                  block
                  outlined
                  color="green lighten-1"
                  class="sick-emp-card-toggle rounded-lg font-weight-bold"
                  style="height: 24px; text-transform: none; font-size: 0.7rem; border-color: rgba(102, 187, 106, 0.4) !important"
                  @click="$emit('show-sick-column', emp.user)">
                  Einblenden
                </v-btn>
                <v-btn
                  v-else
                  x-small
                  block
                  outlined
                  color="red lighten-1"
                  class="sick-emp-card-toggle rounded-lg font-weight-bold"
                  style="height: 24px; text-transform: none; font-size: 0.7rem; border-color: rgba(255, 82, 82, 0.4) !important"
                  @click="$emit('hide-sick-column', emp.user)">
                  Ausblenden
                </v-btn>
              </div>
            </div>

            <div class="pa-3 text-left sick-emp-shifts">
              <div
                style="
                  font-size: 0.7rem;
                  font-weight: 800;
                  color: var(--text-muted);
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                  margin-bottom: 6px;
                ">
                Schichten (nächste 7 Tage):
              </div>

              <div v-if="(emp.nextShifts || emp.shifts) && (emp.nextShifts || emp.shifts).length > 0">
                <div
                  v-for="(sh, shIdx) in emp.nextShifts || emp.shifts"
                  :key="shIdx"
                  class="d-flex flex-column py-1.5 px-2 rounded-lg mb-1.5"
                  style="background: rgba(0, 0, 0, 0.03); border: 1px solid var(--border-light)">
                  <div class="d-flex justify-space-between align-center">
                    <span style="font-weight: 800; font-size: 0.75rem; color: var(--text-main)">
                      {{ $moment(sh.date).format("dd, DD.MM.YYYY") }}
                    </span>
                    <span style="font-size: 0.7rem; font-weight: 600; color: var(--text-muted)">
                      {{ sh.studio }}
                    </span>
                  </div>
                  <div class="d-flex justify-space-between align-center mt-1">
                    <span style="font-size: 0.75rem; font-weight: 700; color: #ff5252">
                      <v-icon x-small color="red" class="mr-0.5" style="margin-top: -2px">mdi-briefcase-outline</v-icon>
                      {{ sh.name || "Schicht" }}
                    </span>
                  </div>
                </div>
              </div>
              <div style="color: var(--text-muted); font-size: 0.75rem; font-style: italic" v-else>Keine Schichten in den nächsten 7 Tagen.</div>
            </div>
          </v-card>
        </div>
      </div>

      <!-- Grid Lines -->
      <div v-for="(slot, idx) in timeSlots" :key="'g' + idx" class="grid-line" :style="{ top: slot.top + 'px' }"></div>
      <!-- Shift Backgrounds -->
      <div
        v-for="(bg, idx) in col.backgrounds"
        :key="'bg' + idx"
        class="shift-background d-flex align-center justify-center"
        :class="{ 'shift-sick': bg.isSick, 'shift-interactive': !bg.isSick }"
        :style="
          bg.isSick
            ? getSickBackgroundStyle(bg, isDarkMode)
            : {
                top: bg.top + 'px',
                height: bg.height + 'px',
                backgroundColor: monochromeShifts
                  ? `color-mix(in srgb, ${monochromeShiftColor || (isDarkMode ? '#222' : '#f1f5f9')} ${monochromeShiftOpacity}%, transparent)`
                  : isDarkMode
                  ? `color-mix(in srgb, ${bg.color} 12%, #1e1e1e)`
                  : `color-mix(in srgb, ${bg.color} 12%, #ffffff)`,
                borderLeft: monochromeShifts
                  ? '3px solid ' + (isDarkMode ? 'rgba(160,160,160,0.25)' : 'rgba(120,120,120,0.3)')
                  : '3px solid ' + bg.color,
              }
        ">
        <div
          v-if="bg.isSick"
          class="sick-watermark text-center font-weight-black select-none"
          :style="{ color: getAbsenceConfig(bg.absenceReason || col.absenceReason).color + 'bb' }">
          <v-icon :color="getAbsenceConfig(bg.absenceReason || col.absenceReason).color" class="mr-1" style="font-size: 1.2rem">
            {{ getAbsenceConfig(bg.absenceReason || col.absenceReason).icon }}
          </v-icon>
          <template v-if="bg.sickPercentage !== undefined && bg.sickPercentage < 100">
            {{ getAbsenceConfig(bg.absenceReason || col.absenceReason).text.toUpperCase() }} ({{ bg.sickPercentage }}%)
          </template>
          <template v-else>
            {{ getAbsenceConfig(bg.absenceReason || col.absenceReason).text.toUpperCase() }}
          </template>
        </div>
        <!-- Shift Drag Handles: der konkrete Pointer entscheidet, nicht die Geräteklasse. -->
        <template v-if="!bg.isSick">
          <div class="shift-resize-handle top" @pointerdown="startDesktopShiftResize($event, bg, 'top')" title="Schicht-Anfang verschieben"></div>
          <div class="shift-resize-handle bottom" @pointerdown="startDesktopShiftResize($event, bg, 'bottom')" title="Schicht-Ende verschieben"></div>
        </template>
      </div>

      <!-- Ghost Shift Background (Resize Preview) -->
      <div
        v-if="shiftResizeData"
        class="shift-background shift-resize-ghost d-flex align-center justify-center"
        :style="{
          top: shiftResizeData.top + 'px',
          height: shiftResizeData.height + 'px',
          backgroundColor: 'rgba(255, 152, 0, 0.25)',
          borderLeft: '4px dashed #FF9800',
          zIndex: 20,
        }">
        <div class="text-center font-weight-black white--text" style="font-size: 0.85rem; text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5)">
          {{ shiftResizeData.newStartStr }} - {{ shiftResizeData.newEndStr }}
        </div>
      </div>

      <!-- Appointment/Task Blocks -->
      <template v-if="isVisible">
        <v-menu
          v-for="(block, bIdx) in col.items"
          :key="'blk' + bIdx"
          open-on-hover
          :open-delay="400"
          :close-delay="150"
          :left="isLastColumn"
          :right="!isLastColumn"
          offset-x
          content-class="wc-appt-preview-menu"
          :disabled="
            contextMenuActive ||
            suppressHoverPreview ||
            !block.isAppt ||
            isDraggingOrResizing ||
            !hasHoverSupport ||
            !block.rawData ||
            (!block.rawData.customer && !block.rawData.noAccountData && !block.description)
          "
          :attach="true"
          transition="scale-transition"
          origin="top center"
          @input="
            (isOpen) => {
              if (isOpen) $emit('hover-block', block)
            }
          ">
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              v-on="on"
              class="wc-block"
              :data-wc-block-id="String(block._id || '')"
              :data-wc-block-type="String(block.type || '')"
              @mouseenter="onBlockMouseEnter(block)"
              :class="[
                block.class,
                {
                  'status-cancelled': block.status === 'cancelled',
                  'status-checkedin': block.status === 'checkedin',
                  'status-noshow': block.status === 'noshow',
                  'wc-block-small': block.height < 55,
                  'wc-block-medium': block.height >= 55 && block.height < 90,
                  'wc-block-past': getBlockTimeStatus(block) === 'past' && block.status !== 'cancelled' && block.status !== 'noshow',
                  'wc-block-upcoming': getBlockTimeStatus(block) === 'upcoming',
                  'wc-block-live': getBlockTimeStatus(block) === 'live',
                  'wc-block-dragging': draggingBlockId === block._id,
                  'wc-block-sick-parent': isFullySick && (block.isAppt || block.isTask),
                },
              ]"
              :style="{
                top: block.top + 'px',
                height: block.height + 'px',
                borderLeft: `4px solid ${block.borderColor}`,
                ...block.style,
                color: isDarkMode ? '' : 'black !important',
                '--hover-shadow-color': block.borderColor + '55',
              }"
              :draggable="block.type !== 'system' && nativeBlockDragEnabled"
              @dragstart.stop="onBlockDragStart($event, block)"
              @dragend="onBlockDragEnd"
              @pointerdown="onBlockPointerDown($event, block, col)"
              @click.capture="onTouchCompatibilityClickCapture"
              @click="onBlockClick($event, block, col)"
              @contextmenu.prevent="onContextMenu($event, block, col)">
              <!-- 🔴 Editing Presence Indicator -->
              <transition name="editing-fade">
                <div v-if="editingUsers && editingUsers[block._id]" class="wc-editing-indicator">
                  <div class="wc-editing-pulse"></div>
                  <div class="wc-editing-badge">
                    <v-icon x-small color="white" style="font-size: 10px">mdi-pencil</v-icon>
                    <span>{{ editingUsers[block._id].userName }}</span>
                  </div>
                </div>
              </transition>
              <!-- Small Block (<55px) -->
              <div
                v-if="block.height < 55"
                class="wc-block-inner d-flex flex-column justify-center px-2"
                style="height: 100%; padding-top: 2px; padding-bottom: 2px">
                <div class="d-flex align-center" :style="{ marginBottom: block.subtitle ? '1px' : '0' }">
                  <v-icon x-small color="red" class="mr-1" v-if="isFullySick && (block.isAppt || block.isTask)" title="Mitarbeiter krank!"
                    >mdi-alert</v-icon
                  >
                  <v-icon x-small :color="block.iconColor" class="mr-1" style="font-size: 12px" v-else>{{ block.icon }}</v-icon>

                  <span class="wc-block-time mr-2" :style="!isDarkMode ? 'color: black; font-weight: bold' : ''">{{ block.start }}</span>
                  <span
                    class="wc-block-title flex-grow-1 text-truncate font-weight-bold"
                    :style="!isDarkMode ? 'color: black; font-size: 0.85rem; line-height: 1.1;' : 'font-size: 0.85rem; line-height: 1.1;'"
                    title=""
                    :title="''"
                    >{{ block.isAppt ? getApptDetails(block).title : block.title }}</span
                  >

                  <v-icon
                    v-if="block.status === 'checkedin' || block.status === 'completed' || block.status === 'done'"
                    x-small
                    color="primary"
                    style="font-size: 12px; margin-left: 4px">
                    mdi-check-circle
                  </v-icon>
                  <v-icon
                    v-if="block.rawData && block.rawData.isAdjusted"
                    x-small
                    color="teal accent-4"
                    class="ml-1"
                    title="Termin wurde angepasst / Interaktion durchgeführt"
                    style="font-size: 12px">
                    mdi-pencil-box-multiple
                  </v-icon>
                </div>
                <div
                  v-if="block.isAppt && getApptDetails(block).name && block.height >= 40"
                  class="wc-block-subtitle text-truncate font-weight-bold"
                  style="font-size: 0.74rem; line-height: 1.1; margin-left: 18px"
                  :style="!isDarkMode ? 'color: #000000 !important; opacity: 1;' : 'color: #ffffff !important; opacity: 0.9;'"
                  title=""
                  :title="''">
                  {{ getApptDetails(block).name }}
                </div>
                <div
                  v-else-if="!block.isAppt && (block.subtitle || block.description) && block.height >= 40"
                  class="wc-block-subtitle text-truncate"
                  :class="{ 'font-weight-bold': block.subtitle === 'Fix' }"
                  style="font-size: 0.74rem; line-height: 1.1; margin-left: 18px"
                  :style="
                    !isDarkMode
                      ? block.subtitle === 'Fix'
                        ? 'color: #000000 !important; opacity: 1;'
                        : 'color: #333333 !important; opacity: 0.75; font-weight: 500;'
                      : block.subtitle === 'Fix'
                      ? 'color: #ffffff !important; opacity: 0.9;'
                      : 'opacity: 0.75; font-weight: 500;'
                  ">
                  <v-tooltip bottom v-if="block.description" max-width="320" :disabled="contextMenuActive">
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        {{ block.subtitle || block.description }}
                      </span>
                    </template>
                    <span>{{ block.description }}</span>
                  </v-tooltip>
                  <span v-else>{{ block.subtitle }}</span>
                </div>
              </div>

              <!-- Normal Block -->
              <div v-else class="wc-block-inner d-flex flex-column pa-2" style="height: 100%">
                <div class="d-flex align-center justify-space-between mb-1">
                  <div class="d-flex align-center">
                    <v-icon
                      x-small
                      color="red"
                      class="mr-1"
                      v-if="isFullySick && (block.isAppt || block.isTask)"
                      style="font-size: 13px"
                      title="Mitarbeiter krank!"
                      >mdi-alert</v-icon
                    >
                    <v-icon x-small :color="block.iconColor" class="mr-1" style="font-size: 13px" v-else>{{ block.icon }}</v-icon>
                    <span class="wc-block-time" :style="!isDarkMode ? 'color: black; font-weight: bold' : ''"
                      >{{ block.start }} - {{ block.end }}</span
                    >
                  </div>
                  <!-- Status indicator -->
                  <div class="d-flex align-center">
                    <v-icon
                      v-if="block.status === 'checkedin' || block.status === 'completed' || block.status === 'done'"
                      x-small
                      color="primary"
                      style="font-size: 12px">
                      mdi-check-circle
                    </v-icon>
                    <v-icon v-else-if="block.status === 'noshow'" x-small color="red accent-2" style="font-size: 12px">mdi-alert-circle</v-icon>
                    <div v-else-if="getBlockTimeStatus(block) === 'upcoming'" class="wc-appt-upcoming-badge">
                      <v-icon style="font-size: 9px; color: #ffca28" class="mr-1">mdi-clock-fast</v-icon>in {{ getMinutesUntil(block.start) }} min
                    </div>
                    <v-icon
                      v-else-if="getBlockTimeStatus(block) === 'past' && block.status !== 'cancelled'"
                      x-small
                      color="rgba(255,255,255,0.4)"
                      style="font-size: 12px"
                      title="Vorbei"
                      >mdi-check</v-icon
                    >
                    <div v-else-if="getBlockTimeStatus(block) === 'live'" class="wc-appt-live-badge">LIVE</div>

                    <v-icon
                      v-if="block.rawData && block.rawData.isAdjusted"
                      x-small
                      color="teal accent-4"
                      class="ml-1"
                      title="Termin wurde angepasst / Interaktion durchgeführt"
                      style="font-size: 12px">
                      mdi-pencil-box-multiple
                    </v-icon>
                  </div>
                </div>

                <template v-if="block.isAppt">
                  <!-- 1. Appointment Title (Largest) -->
                  <div
                    class="wc-block-title text-truncate font-weight-bold"
                    style="font-size: 0.95rem; line-height: 1.2; font-weight: 800"
                    :style="!isDarkMode ? 'color: #000000 !important; opacity: 1;' : 'color: #ffffff !important; opacity: 1;'"
                    title=""
                    :title="''">
                    {{ getApptDetails(block).title }}
                  </div>
                  <!-- 2. Guest Name (below title, larger and darker/black in light mode) -->
                  <div
                    v-if="getApptDetails(block).name && block.height >= 70"
                    class="wc-block-subtitle text-truncate font-weight-bold mt-1"
                    style="font-size: 0.78rem; line-height: 1.2"
                    :style="!isDarkMode ? 'color: #000000 !important; opacity: 1;' : 'color: #ffffff !important; opacity: 0.95;'"
                    title=""
                    :title="''">
                    {{ getApptDetails(block).name }}
                  </div>
                  <!-- 3. Notes / Description (ab 85px height) -->
                  <div
                    v-if="block.height >= 85 && getApptDetails(block).info"
                    class="wc-block-description mt-1"
                    :class="{ 'text-truncate': block.height < 105 }"
                    style="font-size: 0.76rem; line-height: 1.2; opacity: 0.75; font-weight: 500"
                    :style="[
                      !isDarkMode ? { color: '#333333 !important' } : {},
                      block.height >= 105
                        ? {
                            display: '-webkit-box',
                            '-webkit-line-clamp': block.height >= 130 ? '3' : '2',
                            '-webkit-box-orient': 'vertical',
                            overflow: 'hidden',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }
                        : {},
                    ]"
                    title=""
                    :title="''">
                    {{ getApptDetails(block).info }}
                  </div>
                </template>
                <template v-else>
                  <!-- 1. Title (Largest) -->
                  <div
                    class="wc-block-title text-truncate font-weight-bold"
                    style="font-size: 0.95rem; line-height: 1.2; font-weight: 800"
                    :style="!isDarkMode ? 'color: #000000 !important; opacity: 1;' : 'color: #ffffff !important; opacity: 1;'">
                    {{ block.title }}
                  </div>
                  <!-- 2. Subtitle / Type (e.g. Fix for opening shifts) -->
                  <div
                    v-if="block.subtitle && block.height >= 70"
                    class="wc-block-subtitle text-truncate font-weight-bold mt-1"
                    style="font-size: 0.78rem; line-height: 1.2"
                    :style="!isDarkMode ? 'color: #000000 !important; opacity: 1;' : 'color: #ffffff !important; opacity: 0.95;'">
                    {{ block.subtitle }}
                  </div>
                  <!-- 3. Notes / Description (Directly below Title or Subtitle) -->
                  <v-tooltip bottom v-if="block.description && block.height >= 85" max-width="320" :disabled="contextMenuActive">
                    <template v-slot:activator="{ on, attrs }">
                      <div
                        v-bind="attrs"
                        v-on="on"
                        class="wc-block-description mt-1"
                        :class="{ 'text-truncate': block.height < 105 }"
                        style="font-size: 0.76rem; line-height: 1.2; opacity: 0.75; font-weight: 500"
                        :style="[
                          !isDarkMode ? { color: '#333333 !important' } : {},
                          block.height >= 105
                            ? {
                                display: '-webkit-box',
                                '-webkit-line-clamp': block.height >= 130 ? '3' : '2',
                                '-webkit-box-orient': 'vertical',
                                overflow: 'hidden',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              }
                            : {},
                        ]">
                        {{ block.description }}
                      </div>
                    </template>
                    <span>{{ block.description }}</span>
                  </v-tooltip>
                </template>
              </div>

              <!-- Hover Quick Actions -->
              <div class="wc-block-actions" v-if="supportsDesktopEditing && block.type !== 'system'">
                <v-btn
                  v-if="(block.isAppt || block.isTask) && block.status !== 'checkedin' && block.status !== 'completed'"
                  icon
                  x-small
                  class="glass-btn elevation-2 mr-1"
                  style="background: rgba(255, 255, 255, 0.9); border: 1px solid rgba(0, 200, 83, 0.5)"
                  @click.stop.prevent="$emit('quick-check', block)"
                  title="Direkt als Erledigt / Erschienen markieren">
                  <v-icon x-small color="green accent-4">mdi-check-bold</v-icon>
                </v-btn>
              </div>

              <!-- Resize handles: Touch bleibt beim Langdruck, Maus/Trackpad darf direkt ziehen. -->
              <template v-if="block.type !== 'system'">
                <div
                  class="wc-resize-handle top"
                  @pointerdown="startDesktopResize($event, block, 'top')"
                  @click.stop.prevent
                  title="Anfang verschieben"></div>
                <div
                  class="wc-resize-handle bottom"
                  @pointerdown="startDesktopResize($event, block, 'bottom')"
                  @click.stop.prevent
                  title="Ende verschieben"></div>
              </template>
            </div>
          </template>

          <!-- GORGEOUS HOVER PREVIEW CARD (Apple style) -->
          <v-card
            class="wc-appt-preview-card"
            :class="{ 'wc-preview-compact': !block.rawData || (!block.rawData.customer && !block.rawData.noAccountData) }"
            outlined
            :dark="isDarkMode"
            @pointerdown="clearCompletedLongPress"
            @click.stop="$emit('click-item', block, col)"
            @contextmenu.prevent.stop="onContextMenu($event, block, col)"
            style="cursor: pointer">
            <!-- Top line indicator using appointment color -->
            <div class="wc-preview-header pa-3.5 d-flex flex-column" :style="`border-left: 5px solid ${block.borderColor}`">
              <div class="d-flex align-center justify-space-between mb-1.5">
                <div class="d-flex align-center" style="min-width: 0">
                  <v-icon small :color="block.borderColor" class="mr-2">{{ block.icon }}</v-icon>
                  <span class="text-overline font-weight-black tracking-wide text-truncate" :style="`color: ${block.borderColor}`">
                    {{ block.isAppt ? getApptDetails(block).title || "Termin" : block.title }}
                  </span>
                </div>
                <v-chip x-small outlined :color="block.borderColor" class="font-weight-black flex-shrink-0">
                  {{ Math.round((block.height / hourHeight) * 60) }} Min.
                </v-chip>
              </div>
              <div class="wc-preview-time">
                <v-icon x-small class="mr-1.5" style="opacity: 0.6">mdi-clock-outline</v-icon>
                <span style="font-size: 0.78rem; font-weight: 600">{{ block.start }} - {{ block.end }}</span>
              </div>
            </div>

            <!-- Customer Details Block -->
            <div class="pa-4" v-if="block.rawData && (block.rawData.customer || block.rawData.noAccountData)" style="padding-bottom: 8px !important">
              <div class="d-flex flex-row" style="gap: 20px">
                <!-- LEFT COLUMN: Identity, Membership & Contact -->
                <div class="d-flex flex-column" style="flex: 1 1 50%; min-width: 0">
                  <!-- Standard Identity Row -->
                  <div class="d-flex align-center mb-3">
                    <v-avatar size="44" class="mr-3 elevation-2" style="border: 2px solid var(--border-light)">
                      <img
                        v-if="block.rawData.customer && hasRealCustomerPicture(block.rawData.customer)"
                        :src="getCustomerPicture(block.rawData.customer)"
                        style="object-fit: cover" />
                      <div
                        v-else
                        class="d-flex align-center justify-center white--text font-weight-bold"
                        :style="`width: 100%; height: 100%; background: linear-gradient(135deg, ${block.borderColor || '#4CAF50'} 0%, ${
                          block.borderColor ? block.borderColor + '99' : '#81C784'
                        } 100%); font-size: 1rem;`">
                        {{ getCustomerInitials(block.rawData.customer, block.rawData.noAccountData) }}
                      </div>
                    </v-avatar>
                    <div style="min-width: 0">
                      <div class="font-weight-black text-body-2 text-truncate text-main" style="line-height: 1.2">
                        {{
                          block.rawData.customer
                            ? `${block.rawData.customer.vorname} ${block.rawData.customer.nachname}`
                            : `${block.rawData.noAccountData.firstName} ${block.rawData.noAccountData.lastName}`
                        }}
                      </div>
                      <div class="caption text-muted mt-0.5 d-flex align-center">
                        <v-chip
                          x-small
                          v-if="!block.rawData.customer"
                          color="orange lighten-4"
                          class="orange--text text--darken-3 font-weight-black py-0 px-1.5 mr-1"
                          label
                          >Gast</v-chip
                        >
                        <span v-else style="font-size: 0.68rem; opacity: 0.8">Mitglied</span>
                      </div>
                    </div>
                  </div>

                  <!-- ASYNC EXTRA DETAILS SHIMMER LOADER - LEFT COLUMN -->
                  <div
                    v-if="
                      block.rawData.customer?._id &&
                      (!hoveredCustomerDetails || hoveredCustomerDetails.customerId !== block.rawData.customer._id || hoveredCustomerDetails.loading)
                    ">
                    <div class="wc-shimmer wc-card-shimmer mb-3" style="height: 64px; border-radius: 12px"></div>
                  </div>

                  <!-- FULL ASYNC EXTRA DETAILS - LEFT COLUMN -->
                  <div
                    v-else-if="
                      block.rawData.customer?._id &&
                      hoveredCustomerDetails &&
                      hoveredCustomerDetails.customerId === block.rawData.customer._id &&
                      hoveredCustomerDetails.details
                    ">
                    <!-- Sleek Minimalist Membership Card -->
                    <div
                      class="wc-mini-membership-card mb-3 pa-3 rounded-lg"
                      :style="membershipCardStyle(hoveredCustomerDetails.details.mainContract)">
                      <div class="d-flex align-center justify-space-between mb-1">
                        <span
                          class="text-overline font-weight-black tracking-wide"
                          style="font-size: 0.55rem !important; opacity: 0.8; color: inherit; letter-spacing: 0.8px"
                          >MITGLIEDSKARTE</span
                        >
                        <v-chip
                          x-small
                          :color="
                            hoveredCustomerDetails.details.mainContract?.CONTRACTSTATE === 'Laufend'
                              ? 'rgba(76, 175, 80, 0.15)'
                              : 'rgba(128, 128, 128, 0.15)'
                          "
                          class="font-weight-black"
                          style="color: inherit; border: 1px solid currentColor; font-size: 0.6rem; height: 18px">
                          {{
                            hoveredCustomerDetails.details.mainContract ? hoveredCustomerDetails.details.mainContract.CONTRACTSTATE : "Kein Vertrag"
                          }}
                        </v-chip>
                      </div>
                      <div class="d-flex align-center justify-space-between mt-2">
                        <div style="min-width: 0; color: inherit">
                          <div
                            class="font-weight-black text-truncate"
                            style="font-size: 0.8rem; letter-spacing: 0.3px; line-height: 1.2; color: inherit">
                            {{
                              hoveredCustomerDetails.details.mainContract
                                ? hoveredCustomerDetails.details.mainContract.NAME
                                : "Keine aktive Mitgliedschaft"
                            }}
                          </div>
                          <div style="font-size: 0.65rem; opacity: 0.75; margin-top: 3px; color: inherit">
                            {{
                              hoveredCustomerDetails.details.mainContract
                                ? "Gültig bis: " + formatDate(hoveredCustomerDetails.details.mainContract.TERM_ENDDATE)
                                : "Registrierter Account"
                            }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Contact details Panel -->
                    <div
                      class="wc-preview-contact-panel pa-2 rounded-lg"
                      v-if="hoveredCustomerDetails.details.phone || hoveredCustomerDetails.details.email"
                      style="background: rgba(128, 128, 128, 0.04); border: 1px solid var(--border-light)">
                      <div
                        class="d-flex align-center text-muted text-truncate"
                        v-if="hoveredCustomerDetails.details.phone"
                        style="font-size: 0.72rem">
                        <v-icon x-small class="mr-2" style="opacity: 0.7; font-size: 12px">mdi-phone-outline</v-icon>
                        <span class="text-truncate" style="color: var(--text-main); font-weight: 500">{{
                          hoveredCustomerDetails.details.phone
                        }}</span>
                      </div>
                      <div
                        class="d-flex align-center text-muted text-truncate"
                        :class="{ 'mt-1': hoveredCustomerDetails.details.phone }"
                        v-if="hoveredCustomerDetails.details.email"
                        style="font-size: 0.72rem">
                        <v-icon x-small class="mr-2" style="opacity: 0.7; font-size: 12px">mdi-email-outline</v-icon>
                        <span class="text-truncate" style="color: var(--text-main); font-weight: 500">{{
                          hoveredCustomerDetails.details.email
                        }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Contact fallback for Guest -->
                  <div
                    v-else-if="!block.rawData.customer?._id && (block.rawData.noAccountData?.phone || block.rawData.noAccountData?.email)"
                    class="wc-preview-contact-panel pa-2 rounded-lg"
                    style="background: rgba(128, 128, 128, 0.04); border: 1px solid var(--border-light)">
                    <div class="d-flex align-center text-muted text-truncate" v-if="block.rawData.noAccountData?.phone" style="font-size: 0.72rem">
                      <v-icon x-small class="mr-2" style="opacity: 0.7; font-size: 12px">mdi-phone-outline</v-icon>
                      <span class="text-truncate" style="color: var(--text-main); font-weight: 500">{{ block.rawData.noAccountData.phone }}</span>
                    </div>
                    <div
                      class="d-flex align-center text-muted text-truncate"
                      :class="{ 'mt-1': block.rawData.noAccountData?.phone }"
                      v-if="block.rawData.noAccountData?.email"
                      style="font-size: 0.72rem">
                      <v-icon x-small class="mr-2" style="opacity: 0.7; font-size: 12px">mdi-email-outline</v-icon>
                      <span class="text-truncate" style="color: var(--text-main); font-weight: 500">{{ block.rawData.noAccountData.email }}</span>
                    </div>
                  </div>
                </div>

                <!-- RIGHT COLUMN: Trainingsstatus & Notes -->
                <div class="d-flex flex-column pl-4" style="flex: 1 1 50%; min-width: 0; border-left: 1px solid var(--border-light)">
                  <!-- ASYNC EXTRA DETAILS SHIMMER LOADER - RIGHT COLUMN -->
                  <div
                    v-if="
                      block.rawData.customer?._id &&
                      (!hoveredCustomerDetails || hoveredCustomerDetails.customerId !== block.rawData.customer._id || hoveredCustomerDetails.loading)
                    ">
                    <div class="wc-shimmer wc-line-shimmer mb-2" style="width: 90%; height: 10px"></div>
                    <div class="wc-shimmer wc-line-shimmer mb-2" style="width: 70%; height: 10px"></div>
                    <div class="wc-shimmer wc-line-shimmer mb-4" style="width: 40%; height: 10px"></div>
                    <div class="wc-shimmer wc-line-shimmer" style="width: 100%; height: 32px; border-radius: 8px"></div>
                  </div>

                  <!-- FULL ASYNC EXTRA DETAILS - RIGHT COLUMN -->
                  <div
                    v-else-if="
                      block.rawData.customer?._id &&
                      hoveredCustomerDetails &&
                      hoveredCustomerDetails.customerId === block.rawData.customer._id &&
                      hoveredCustomerDetails.details
                    ">
                    <!-- Trainingsstatus -->
                    <div class="pa-2 rounded-lg wc-preview-flow mb-3" v-if="showTrainingFlow(block)" style="background: rgba(128, 128, 128, 0.03)">
                      <div
                        class="caption font-weight-black mb-2 d-flex align-center text-muted"
                        style="font-size: 0.62rem !important; opacity: 0.6; letter-spacing: 0.5px">
                        <v-icon x-small class="mr-1" style="font-size: 10px">mdi-sync</v-icon> TRAININGSSTATUS
                      </div>
                      <div class="d-flex align-center justify-space-between mb-1.5">
                        <div class="d-flex align-center text-truncate" style="font-size: 0.7rem; min-width: 0">
                          <v-icon
                            x-small
                            :color="hoveredCustomerDetails.details.anamneseInfo.exists ? 'success' : 'orange'"
                            class="mr-1 flex-shrink-0"
                            style="font-size: 11px">
                            {{ hoveredCustomerDetails.details.anamneseInfo.exists ? "mdi-check-circle" : "mdi-alert-circle-outline" }}
                          </v-icon>
                          <span class="font-weight-medium text-truncate">1. Anamnese</span>
                        </div>
                        <span class="caption text-muted font-weight-bold flex-shrink-0 ml-1" style="font-size: 0.62rem">
                          {{ hoveredCustomerDetails.details.anamneseInfo.exists ? "Ja" : "Nein" }}
                        </span>
                      </div>
                      <div class="d-flex align-center justify-space-between">
                        <div class="d-flex align-center text-truncate" style="font-size: 0.7rem; min-width: 0">
                          <v-icon
                            x-small
                            :color="hoveredCustomerDetails.details.activePlanTitle ? 'success' : 'orange'"
                            class="mr-1 flex-shrink-0"
                            style="font-size: 11px">
                            {{ hoveredCustomerDetails.details.activePlanTitle ? "mdi-check-circle" : "mdi-alert-circle-outline" }}
                          </v-icon>
                          <span class="font-weight-medium text-truncate"
                            >2. Plan: {{ hoveredCustomerDetails.details.activePlanTitle || "Kein Plan" }}</span
                          >
                        </div>
                        <span class="caption text-muted font-weight-bold flex-shrink-0 ml-1" style="font-size: 0.62rem">
                          {{ hoveredCustomerDetails.details.activePlanTitle ? "Ja" : "Nein" }}
                        </span>
                      </div>
                    </div>

                    <!-- Attendance (subtle row of last visits) -->
                    <div class="mb-3" v-if="hoveredCustomerDetails.details.lastVisits && hoveredCustomerDetails.details.lastVisits.length > 0">
                      <div
                        class="caption font-weight-black mb-1 d-flex align-center text-muted"
                        style="font-size: 0.62rem !important; opacity: 0.6; letter-spacing: 0.5px">
                        <v-icon x-small class="mr-1" style="font-size: 10px">mdi-history</v-icon> LETZTE BESUCHE
                      </div>
                      <div class="d-flex align-center gap-1.5 overflow-x-auto pb-1 custom-scrollbar">
                        <div
                          v-for="(visit, idx) in hoveredCustomerDetails.details.lastVisits.slice(0, 3)"
                          :key="idx"
                          class="wc-visit-pill px-1.5 py-0.5 rounded d-flex align-center flex-shrink-0"
                          style="background: rgba(128, 128, 128, 0.05); border: 1px solid rgba(128, 128, 128, 0.08); gap: 2px; font-size: 0.62rem">
                          <span style="font-weight: 700; color: var(--text-main)">{{ formatDateDayMonth(visit.date) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Notes / Description (Always visible on right side if exists) -->
                  <div
                    class="pa-2 rounded-lg wc-preview-notes fill-height d-flex flex-column"
                    v-if="block.description || getApptDetails(block).info"
                    style="background: rgba(128, 128, 128, 0.03); flex-grow: 1">
                    <div
                      class="caption font-weight-black mb-1 d-flex align-center text-muted"
                      style="font-size: 0.62rem !important; opacity: 0.6; letter-spacing: 0.5px">
                      <v-icon x-small class="mr-1" style="font-size: 10px">mdi-notebook-outline</v-icon> NOTIZEN
                    </div>
                    <div
                      style="
                        font-size: 0.72rem !important;
                        line-height: 1.35;
                        color: var(--text-main);
                        font-weight: 500;
                        white-space: normal;
                        word-break: break-word;
                      ">
                      {{ block.description || getApptDetails(block).info }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Apple-style Stacked Action Menu -->
            <div class="wc-preview-actions mt-2" v-if="block.rawData?.customer?._id">
              <div class="wc-preview-action-row d-flex align-center pa-3 cursor-pointer" @click.stop="$emit('open-panel', block.rawData.customer)">
                <v-icon small class="mr-3">mdi-parking</v-icon>
                <span style="flex: 1; font-weight: 600; font-size: 0.82rem; color: var(--text-main)">Kundenkarte (Panel) öffnen</span>
                <v-icon small color="grey" style="opacity: 0.5">mdi-chevron-right</v-icon>
              </div>
            </div>
          </v-card>
        </v-menu>
      </template>

      <!-- Ghost Block (Resize Preview) -->
      <div
        v-if="resizeData"
        class="ghost-block d-flex align-end justify-end pa-1"
        style="z-index: 20; background: rgba(33, 150, 243, 0.1); border-color: rgba(33, 150, 243, 0.5)"
        :style="{ top: resizeData.top + 'px', height: resizeData.height + 'px', willChange: 'height' }">
        <span
          class="white--text font-weight-bold"
          style="font-size: 0.85rem; text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6); background: rgba(0, 0, 0, 0.4); padding: 2px 6px; border-radius: 4px"
          >{{ resizeData.newStartStr }} - {{ resizeData.newEndStr }}</span
        >
      </div>

      <!-- Ghost Block (Drag Preview) -->
      <div
        v-if="isHovered"
        class="ghost-block d-flex align-center justify-center flex-column"
        :class="{ 'error-ghost': hoverState.error }"
        :style="{ transform: `translateY(${hoverState.top}px)`, top: 0, height: hoverState.height + 'px', willChange: 'transform' }">
        <span class="white--text font-weight-bold" style="font-size: 1.1rem; text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5)">{{ hoverState.time }}</span>
        <span v-if="hoverState.error" class="wc-ghost-error mt-1">{{ hoverState.error }}</span>
      </div>

      <!-- Click Grid -->
      <div
        class="click-grid-layer"
        @pointerdown.self="startLongPress($event, { type: 'empty', col })"
        @click.capture.self="onTouchCompatibilityClickCapture"
        @click.self="onEmptyClick($event, col)"
        @contextmenu.self.prevent="onEmptyContextMenu($event, col)"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WorkCalendarColumn",
  props: {
    col: Object,
    isDarkMode: Boolean,
    isLastColumn: { type: Boolean, default: false },
    showLastNameInitial: Boolean,
    hoursCount: Number,
    hourHeight: Number,
    headerOffset: Number,
    hoverState: Object,
    editingUsers: { type: Object, default: () => ({}) },
    isToday: Boolean,
    currentDate: [String, Date, Object],
    monochromeShifts: { type: Boolean, default: false },
    colorNoShift: { type: Boolean, default: false },
    noShiftBgColor: { type: String, default: null },
    monochromeShiftColor: { type: String, default: null },
    monochromeShiftOpacity: { type: Number, default: 100 },
    focusedEmployeeId: { type: String, default: null },
    isCompactMode: { type: Boolean, default: false },
    isThickerLines: { type: Boolean, default: false },
    hoveredCustomerDetails: { type: Object, default: () => ({}) },
    sickEmployeesToday: { type: Array, default: () => [] },
    hiddenSickTrainerIds: { type: Array, default: () => [] },
    contextMenuActive: { type: Boolean, default: false },
    timeSlots: { type: Array, default: () => [] },
    isFitMode: { type: Boolean, default: false },
  },
  data() {
    return {
      nowTick: Date.now(), // Reaktiver Timestamp für Live-Updates
      isAbsencesCollapsed: true,
      // Während eines nativen HTML5-Drags darf der Quell-DOM nicht reaktiv
      // verändert werden. Insbesondere Chromium auf Windows-Hybridgeräten
      // beendet den Drag sonst direkt nach dem regulären `pointercancel`.
      draggingBlockId: null,
      isVisible: true, // Startet auf true, wird von IntersectionObserver gesteuert
      observer: null,
      resizeData: null, // Für Drag-to-Resize
      shiftResizeData: null, // Für Shift Drag-to-Resize
      // Touch has one deterministic interaction model: tap opens the entry,
      // holding opens its action menu. A slightly larger tolerance keeps normal
      // finger jitter from cancelling the hold on iPad.
      longPressDelay: 450,
      longPressMoveTolerance: 18,
      suppressHoverPreview: false,
      absenceConfigs: {
        sick: {
          text: "Krank",
          color: "#ff5252",
          lightColor: "#d32f2f",
          bgColorRgb: "244, 67, 54",
          icon: "mdi-emoticon-sick-outline",
          bannerText: "Mitarbeiter krank! Bitte Termine übertragen.",
          cardLabel: "HEUTE KRANK",
        },
        school: {
          text: "Schule",
          color: "var(--v-primary-base)",
          lightColor: "color-mix(in srgb, var(--v-primary-base) 80%, black)",
          bgColorRgb: "var(--v-primary-base)",
          icon: "mdi-school",
          bannerText: "Mitarbeiter in Schule! Bitte Termine übertragen.",
          cardLabel: "HEUTE SCHULE",
        },
        military: {
          text: "Militär",
          color: "#B388FF",
          lightColor: "#7c4dff",
          bgColorRgb: "156, 39, 176",
          icon: "mdi-shield-account",
          bannerText: "Mitarbeiter im Militär! Bitte Termine übertragen.",
          cardLabel: "HEUTE MILITÄR",
        },
        pregnancy: {
          text: "Schwangerschaft / Mutterschaft",
          color: "#FF4081",
          lightColor: "#c2185b",
          bgColorRgb: "233, 30, 99",
          icon: "mdi-baby-carriage",
          bannerText: "Mitarbeiter in Schwangerschaft / Mutterschaft! Bitte Termine übertragen.",
          cardLabel: "HEUTE SCHWANGERSCHAFT / MUTTERSCHAFT",
        },
        holiday: {
          text: "Ferien",
          color: "#4CAF50",
          lightColor: "#388E3C",
          bgColorRgb: "76, 175, 80",
          icon: "mdi-palm-tree",
          bannerText: "Mitarbeiter in den Ferien! Bitte Termine übertragen.",
          cardLabel: "HEUTE FERIEN",
        },
        freewish: {
          text: "Freiwunsch",
          color: "#9C27B0",
          lightColor: "#7B1FA2",
          bgColorRgb: "156, 39, 176",
          icon: "mdi-calendar-heart",
          bannerText: "Mitarbeiter hat Freiwunsch! Bitte Termine übertragen.",
          cardLabel: "HEUTE FREIWUNSCH",
        },
        other: {
          text: "Abwesend",
          color: "#90A4AE",
          lightColor: "#455A64",
          bgColorRgb: "96, 125, 139",
          icon: "mdi-calendar-remove",
          bannerText: "Mitarbeiter abwesend! Bitte Termine übertragen.",
          cardLabel: "HEUTE ABWESEND",
        },
      },
    }
  },
  mounted() {
    this.checkAbsenceCollapseState()

    // Nur ticken wenn wir den heutigen Tag anschauen
    this._ticker = setInterval(() => {
      if (this.isToday) {
        this.nowTick = Date.now()
      }
    }, 30000) // Alle 30 Sekunden

    if (window.IntersectionObserver) {
      setTimeout(() => {
        const el = this.$el && typeof this.$el.closest === "function" ? this.$el : null
        const scroller = el ? el.closest(".wc-scroller") : null
        if (scroller) {
          this.observer = new IntersectionObserver(
            (entries) => {
              if (entries[0]) this.isVisible = entries[0].isIntersecting
            },
            { root: scroller, rootMargin: "4000px 300px" },
          ) // 300px Puffer links und rechts, 4000px vertikal um Ausblenden beim Scrollen zu verhindern
          this.observer.observe(this.$el)
        }
      }, 500) // Verzögert starten, damit Layout initial rendert
    }
  },
  watch: {
    "col.items": {
      immediate: true,
      handler() {
        this.checkAbsenceCollapseState()
      },
    },
  },
  beforeDestroy() {
    this.cancelLongPress(true)
    if (this._deferredTouchActionTimer) clearTimeout(this._deferredTouchActionTimer)
    this._deferredTouchActionTimer = null
    this.clearTouchCompatibilityClickGuard()
    this.cleanupPointerBlockDrag()
    document.removeEventListener("pointermove", this.onResizeMove)
    document.removeEventListener("pointerup", this.onResizeEnd)
    document.removeEventListener("pointercancel", this.onResizeEnd)
    document.removeEventListener("pointermove", this.onShiftResizeMove)
    document.removeEventListener("pointerup", this.onShiftResizeEnd)
    document.removeEventListener("pointercancel", this.onShiftResizeEnd)
    if (this._hoverPreviewReleaseTimer) clearTimeout(this._hoverPreviewReleaseTimer)
    if (this._ticker) clearInterval(this._ticker)
    if (this.observer) this.observer.disconnect()
  },
  computed: {
    isFocused() {
      return this.focusedEmployeeId && String(this.col.id) === String(this.focusedEmployeeId)
    },
    columnStyle() {
      const styles = {}
      if (this.isCompactMode) {
        styles.minWidth = "150px"
        styles.flex = "1 1 150px"
      } else {
        styles.minWidth = "240px"
        styles.flex = "1 1 240px"
      }

      const isHelper = this.col.isHelper || this.col.id === "ma_info" || this.col.id === "no_show" || this.col.id === "unassigned"

      if (!isHelper && this.colorNoShift && this.noShiftBgColor) {
        styles.background = this.noShiftBgColor + " !important"
      }
      return styles
    },
    isDraggingOrResizing() {
      return !!this.draggingBlockId || !!this.resizeData || !!this.shiftResizeData
    },
    isSick() {
      if (this.col.worksDuringHoliday) {
        return !!(this.col.shifts && this.col.shifts.some((s) => s.isSick))
      }
      return !!this.col.isSick || (this.col.shifts && this.col.shifts.some((s) => s.isSick))
    },
    isFullySick() {
      if (this.col.worksDuringHoliday) return false
      // If there's a sick shift, it's only "fully sick" if sickPercentage is not set or >= 100
      if (this.col.shifts && this.col.shifts.length > 0) {
        const sickShift = this.col.shifts.find((s) => s.isSick)
        if (sickShift) {
          return sickShift.sickPercentage === undefined || sickShift.sickPercentage >= 100
        }
      }
      return !!this.col.isSick
    },
    isHovered() {
      return this.hoverState && this.hoverState.colId === this.col.id
    },
    appointmentCount() {
      return (this.col.items || []).filter((b) => b.isAppt).length
    },
    taskCount() {
      return (this.col.items || []).filter((b) => b.isTask).length
    },
    notesCount() {
      return (this.col.notes || []).length
    },
    hasHoverSupport() {
      if (typeof window === "undefined") return true
      return window.matchMedia("(any-hover: hover)").matches
    },
    supportsDesktopEditing() {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true

      // `any-*` intentionally detects an attached mouse/trackpad on hybrid
      // touchscreen devices. A coarse pointer may exist at the same time and
      // must not disable desktop editing. Touch-only devices match neither
      // fine nor hover and continue to use the pointer long-press flow.
      const hasFinePointer = window.matchMedia("(any-pointer: fine)").matches
      const hasHoverPointer = window.matchMedia("(any-hover: hover)").matches
      const hasCoarsePointer = window.matchMedia("(any-pointer: coarse)").matches

      // Fine pointer OR hover is enough for the familiar mouse controls. If a
      // browser does not understand the pointer media queries at all, keep the
      // established desktop controls enabled. Only a genuine touch-only setup
      // (coarse, without fine/hover) uses the long-press interaction instead.
      return hasFinePointer || hasHoverPointer || !hasCoarsePointer
    },
    usesPointerDragFallback() {
      if (typeof navigator === "undefined") return false

      // On every device with touch hardware the native HTML5 drag is disabled.
      // A concrete mouse/trackpad pointer still gets the custom pointer drag;
      // touch and pen are filtered by `shouldStartPointerBlockDrag` and use hold.
      return Number(navigator.maxTouchPoints || 0) > 0
    },
    hasTouchHardware() {
      return typeof navigator !== "undefined" && Number(navigator.maxTouchPoints || 0) > 0
    },
    nativeBlockDragEnabled() {
      // Never let Safari/Chromium decide whether a finger gesture is a native
      // HTML5 drag. On hardware with touch support mouse/trackpad uses our
      // PointerEvent fallback, while touch/pen consistently uses tap/hold.
      return !this.hasTouchHardware
    },
    utilizationPercentage() {
      if (!this.col.backgrounds || this.col.backgrounds.length === 0) return 0
      if (this.col.isHelper) return 0

      let totalShiftHeight = 0
      this.col.backgrounds.forEach((bg) => {
        totalShiftHeight += bg.height
      })

      if (totalShiftHeight === 0) return 0

      let totalOccupiedHeight = 0
      ;(this.col.items || []).forEach((item) => {
        if ((item.isAppt || item.isTask) && item.status !== "cancelled" && item.status !== "noshow") {
          totalOccupiedHeight += item.height
        }
      })

      const percentage = Math.round((totalOccupiedHeight / totalShiftHeight) * 100)
      return Math.min(percentage, 100)
    },
    utilizationColor() {
      const p = this.utilizationPercentage
      if (p >= 85) return "#ff5252" // rot (sehr ausgelastet)
      if (p >= 50) return "#ffb300" // gelb
      return "#4caf50" // grün (viel platz)
    },
  },
  methods: {
    onBlockPointerDown(event, block, col) {
      // Hybrid-Geräte liefern Touch- und Maus-Eingaben. Merken, welcher
      // Eingabeweg diesen konkreten Versuch gestartet hat, damit nie zugleich
      // nativer Desktop-Drag und Touch-Langdruck aktiv werden.
      this._activeBlockPointerType = event && event.pointerType ? event.pointerType : "mouse"

      if (this.shouldStartPointerBlockDrag(event, block)) {
        this.startPointerBlockDrag(event, block, col)
        return
      }
      this.startLongPress(event, { type: "item", block, col })
    },
    shouldStartPointerBlockDrag(event, block) {
      if (!this.usesPointerDragFallback || !event || !block || block.type === "system") return false
      if (event.pointerType && event.pointerType !== "mouse") return false
      if (event.isPrimary === false || (event.button != null && event.button !== 0)) return false

      // Buttons, Links und Eingabefelder im Termin behalten ihre normale
      // Interaktion. Die Resize-Griffe stoppen das Event bereits selbst.
      const target = event.target
      if (
        target &&
        typeof target.closest === "function" &&
        target.closest("button, a, input, textarea, select, [role='button'], .wc-resize-handle")
      ) {
        return false
      }
      return true
    },
    startPointerBlockDrag(event, block, col) {
      this.cleanupPointerBlockDrag()

      const sourceEl = event.currentTarget
      this._pointerBlockDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        clientX: event.clientX,
        clientY: event.clientY,
        sourceEl,
        block,
        col,
        moveData: null,
        active: false,
        finished: false,
        lastTarget: null,
        ctrlKey: !!event.ctrlKey,
      }

      // Pointer-Capture verhindert, dass Chromium beim Verlassen des schmalen
      // Terminblocks die Folgeevents verliert. Das tatsächliche Ziel wird
      // weiterhin per elementFromPoint unter dem Mauszeiger bestimmt.
      if (sourceEl && typeof sourceEl.setPointerCapture === "function" && event.pointerId != null) {
        try {
          sourceEl.setPointerCapture(event.pointerId)
          sourceEl.addEventListener("lostpointercapture", this.onPointerBlockDragLostCapture, true)
        } catch (error) {
          // Pointer-Capture ist optional; die globalen Pointer-Listener
          // übernehmen auf Browsern, die es hier nicht erlauben.
        }
      }

      window.addEventListener("pointermove", this.onPointerBlockDragMove, { capture: true, passive: false })
      window.addEventListener("pointerup", this.onPointerBlockDragEnd, true)
      // Einige Windows-Hybridgeräte liefern beim Trackpad-Drag zwar
      // Pointer-Moves, beim Loslassen aber nur das klassische MouseEvent.
      window.addEventListener("mouseup", this.onPointerBlockDragMouseUp, true)
      window.addEventListener("pointercancel", this.onPointerBlockDragCancel, true)
      window.addEventListener("blur", this.onPointerBlockDragCancel, true)
    },
    createBlockMoveData(block, sourceCol, clientY, sourceEl) {
      let duration = block.rawData?.duration || 60
      if (block.start && block.end && this.$moment) {
        const start = this.$moment(block.start, "HH:mm")
        const end = this.$moment(block.end, "HH:mm")
        duration = end.diff(start, "minutes") || duration
      }

      let dragOffsetY = 0
      if (sourceEl && typeof sourceEl.getBoundingClientRect === "function") {
        const rect = sourceEl.getBoundingClientRect()
        dragOffsetY = clientY - rect.top
      }

      return {
        _id: block._id,
        isTask: !!block.isTask,
        isAppt: !!block.isAppt,
        isPause: block.type === "pause",
        title: block.title,
        start: block.start,
        end: block.end,
        duration,
        sourceColId: sourceCol.id,
        dragOffsetY,
        parentShiftId: block.parentShiftId || block.rawData?.parentShiftId,
        timeIndex: block.timeIndex,
        rawData: block.rawData || null,
        type: block.type,
      }
    },
    getPointerBlockDragTarget(clientX, clientY) {
      if (typeof document === "undefined" || typeof document.elementFromPoint !== "function") return null
      const hit = document.elementFromPoint(clientX, clientY)
      const columnEl = hit && typeof hit.closest === "function" ? hit.closest(".employee-column") : null
      const laneBody = columnEl ? columnEl.querySelector(".lane-body") : null
      if (!columnEl || !laneBody) return null

      const rect = laneBody.getBoundingClientRect()
      if (clientY < rect.top || clientY > rect.bottom) return null
      return {
        columnEl,
        laneBody,
        colId: columnEl.getAttribute("data-wc-column-id"),
        relativeY: clientY - rect.top,
      }
    },
    onPointerBlockDragMove(event) {
      const drag = this._pointerBlockDrag
      if (!drag || event.pointerId !== drag.pointerId) return
      drag.clientX = event.clientX
      drag.clientY = event.clientY
      drag.ctrlKey = !!event.ctrlKey

      if (!drag.active) {
        const deltaX = event.clientX - drag.startX
        const deltaY = event.clientY - drag.startY
        if (deltaX * deltaX + deltaY * deltaY < 36) return

        // Linke Taste wurde vor Erreichen des Schwellwerts losgelassen.
        if (event.buttons != null && (event.buttons & 1) === 0) {
          this.cleanupPointerBlockDrag()
          return
        }

        drag.active = true
        drag.moveData = this.createBlockMoveData(drag.block, drag.col, drag.startY, drag.sourceEl)
        this.draggingBlockId = drag.block._id
        this.holdHoverPreviewSuppression()
        if (typeof window !== "undefined" && window.getSelection) {
          const selection = window.getSelection()
          if (selection && typeof selection.removeAllRanges === "function") selection.removeAllRanges()
        }
        this.$emit("block-drag-start", drag.moveData)
      }

      if (event.cancelable) event.preventDefault()
      const target = this.getPointerBlockDragTarget(event.clientX, event.clientY)
      // Das zuletzt tatsächlich getroffene Ziel ist zugleich das Ziel, für
      // das der Benutzer den grünen Rahmen sieht. Es dient als Fallback, falls
      // das abschliessende Release-Event keine brauchbaren Koordinaten enthält.
      if (!target || target.colId == null) return
      drag.lastTarget = target

      this.$emit("drag-over", {
        event,
        colId: target.colId,
        relativeY: target.relativeY,
      })
    },
    onPointerBlockDragEnd(event) {
      this.finalizePointerBlockDrag(event, "pointerup")
    },
    onPointerBlockDragMouseUp(event) {
      if (event && event.button != null && event.button !== 0) return
      this.finalizePointerBlockDrag(event, "mouseup")
    },
    onPointerBlockDragLostCapture(event) {
      const drag = this._pointerBlockDrag
      if (!drag || drag.finished || (event?.pointerId != null && event.pointerId !== drag.pointerId)) return

      // lostpointercapture wird regulär direkt nach pointerup ausgelöst. Ist
      // die Primärtaste bereits frei, muss es den Abschluss übernehmen dürfen.
      // Bei buttons=1 ging nur die Capture-Verbindung verloren; dann verfolgen
      // die Window-Listener den weiterhin gedrückten Zeiger bis mouseup weiter.
      if (event?.buttons == null || (event.buttons & 1) === 0) {
        this.finalizePointerBlockDrag(event, "lostpointercapture")
      }
    },
    finalizePointerBlockDrag(event, releaseSource) {
      const drag = this._pointerBlockDrag
      if (!drag) {
        return
      }
      if (drag.finished) {
        return
      }
      if (event?.pointerId != null && drag.pointerId != null && event.pointerId !== drag.pointerId) return

      // Vor Emit/Cleanup sperren: pointerup, mouseup und lostpointercapture
      // können für dieselbe physische Aktion unmittelbar nacheinander kommen.
      drag.finished = true
      const wasActive = drag.active
      // lostpointercapture enthält je nach Windows-Treiber 0/0 statt der
      // letzten Mausposition. In diesem Fall sind die gespeicherten Move-
      // Koordinaten die verlässliche Quelle.
      const hasReleaseCoordinates = releaseSource !== "lostpointercapture" && Number.isFinite(event?.clientX) && Number.isFinite(event?.clientY)
      const releaseX = hasReleaseCoordinates ? event.clientX : drag.clientX
      const releaseY = hasReleaseCoordinates ? event.clientY : drag.clientY
      const releaseTarget = wasActive ? this.getPointerBlockDragTarget(releaseX, releaseY) : null
      const target = releaseTarget || (wasActive ? drag.lastTarget : null)
      const targetRect = target?.laneBody?.getBoundingClientRect?.()
      const dropClientY = releaseTarget || !targetRect || !Number.isFinite(target?.relativeY) ? releaseY : targetRect.top + target.relativeY

      if (wasActive) {
        if (event?.cancelable && typeof event.preventDefault === "function") event.preventDefault()
        if (typeof event?.stopPropagation === "function") event.stopPropagation()
        this._suppressPointerBlockClick = {
          blockId: String(drag.block?._id || ""),
          until: Date.now() + 700,
        }

        if (target && target.colId != null) {
          this.$emit("pointer-block-drop", {
            pointerEvent: event,
            moveData: drag.moveData,
            targetColId: target.colId,
            targetColumnEl: target.columnEl,
            laneBody: target.laneBody,
            clientX: releaseX,
            clientY: dropClientY,
            relativeY: target.relativeY,
            ctrlKey: event?.ctrlKey != null ? !!event.ctrlKey : !!drag.ctrlKey,
            releaseSource,
          })
        }
      }

      this.cleanupPointerBlockDrag()
      if (wasActive) this.$emit("block-drag-end")
    },
    onPointerBlockDragCancel(event) {
      const drag = this._pointerBlockDrag
      if (!drag || (event.pointerId != null && event.pointerId !== drag.pointerId)) return
      const wasActive = drag.active

      // Manche Hybrid-Treiber melden das physische Loslassen als
      // pointercancel statt pointerup. Bei einem bereits aktiven Maus-Drag,
      // freier Primärtaste und einem sichtbar markierten Ziel ist das semantisch
      // ein Drop. blur und ein Cancel bei weiterhin gedrückter Taste bleiben
      // dagegen echte Abbrüche.
      if (event?.type === "pointercancel" && wasActive && drag.lastTarget && (event.buttons == null || (event.buttons & 1) === 0)) {
        this.finalizePointerBlockDrag(event, "pointercancel-release")
        return
      }
      this.cleanupPointerBlockDrag()
      if (wasActive) this.$emit("block-drag-end")
    },
    cleanupPointerBlockDrag() {
      if (typeof window !== "undefined") {
        window.removeEventListener("pointermove", this.onPointerBlockDragMove, true)
        window.removeEventListener("pointerup", this.onPointerBlockDragEnd, true)
        window.removeEventListener("mouseup", this.onPointerBlockDragMouseUp, true)
        window.removeEventListener("pointercancel", this.onPointerBlockDragCancel, true)
        window.removeEventListener("blur", this.onPointerBlockDragCancel, true)
      }

      const drag = this._pointerBlockDrag
      // Zuerst Zustand und Listener lösen. releasePointerCapture kann synchron
      // ein lostpointercapture auslösen, das sonst einen zweiten Drop versuchen
      // würde.
      this._pointerBlockDrag = null
      if (drag?.sourceEl && typeof drag.sourceEl.removeEventListener === "function") {
        drag.sourceEl.removeEventListener("lostpointercapture", this.onPointerBlockDragLostCapture, true)
      }
      if (drag?.sourceEl && typeof drag.sourceEl.releasePointerCapture === "function" && drag.pointerId != null) {
        try {
          if (!drag.sourceEl.hasPointerCapture || drag.sourceEl.hasPointerCapture(drag.pointerId)) {
            drag.sourceEl.releasePointerCapture(drag.pointerId)
          }
        } catch (error) {
          // Das Element kann während eines Re-Renders bereits abgelöst sein.
        }
      }

      this.draggingBlockId = null
      this._activeBlockPointerType = null
      this.releaseHoverPreviewSuppression()
    },
    isLongPressPointer(event) {
      return !!event && (event.pointerType === "touch" || event.pointerType === "pen")
    },
    holdHoverPreviewSuppression() {
      if (this._hoverPreviewReleaseTimer) clearTimeout(this._hoverPreviewReleaseTimer)
      this._hoverPreviewReleaseTimer = null
      this.suppressHoverPreview = true
    },
    releaseHoverPreviewSuppression() {
      if (this._hoverPreviewReleaseTimer) clearTimeout(this._hoverPreviewReleaseTimer)
      this._hoverPreviewReleaseTimer = setTimeout(() => {
        this.suppressHoverPreview = false
        this._hoverPreviewReleaseTimer = null
      }, 350)
    },
    startLongPress(event, payload) {
      this.clearCompletedLongPress()
      if (!this.isLongPressPointer(event) || event.isPrimary === false || (event.button != null && event.button !== 0)) return
      if (!payload || (payload.type === "item" && (!payload.block || payload.block.type === "system"))) return
      if (this._longPress) return

      this.holdHoverPreviewSuppression()
      this._longPress = {
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        clientX: event.clientX,
        clientY: event.clientY,
        target: event.currentTarget,
        payload,
        triggered: false,
        nativeContextConsumed: false,
      }
      this.bindLongPressGuards()
      this._longPressTimer = setTimeout(() => this.triggerLongPress(), this.longPressDelay)
    },
    bindLongPressGuards() {
      if (this._longPressGuardsBound || typeof window === "undefined") return
      this._longPressGuardsBound = true
      window.addEventListener("pointerdown", this.onLongPressAdditionalPointer, true)
      window.addEventListener("pointermove", this.onLongPressPointerMove, true)
      window.addEventListener("pointerup", this.onLongPressPointerEnd, true)
      window.addEventListener("pointercancel", this.onLongPressPointerEnd, true)
      window.addEventListener("scroll", this.onLongPressScroll, true)
    },
    unbindLongPressGuards() {
      if (!this._longPressGuardsBound || typeof window === "undefined") return
      this._longPressGuardsBound = false
      window.removeEventListener("pointerdown", this.onLongPressAdditionalPointer, true)
      window.removeEventListener("pointermove", this.onLongPressPointerMove, true)
      window.removeEventListener("pointerup", this.onLongPressPointerEnd, true)
      window.removeEventListener("pointercancel", this.onLongPressPointerEnd, true)
      window.removeEventListener("scroll", this.onLongPressScroll, true)
    },
    onLongPressAdditionalPointer(event) {
      if (this._longPress && event.pointerId !== this._longPress.pointerId && !this._longPress.triggered) this.cancelLongPress()
    },
    onLongPressPointerMove(event) {
      const press = this._longPress
      if (!press || event.pointerId !== press.pointerId) return
      press.clientX = event.clientX
      press.clientY = event.clientY
      if (press.triggered) {
        if (event.cancelable) event.preventDefault()
        return
      }
      const deltaX = event.clientX - press.startX
      const deltaY = event.clientY - press.startY
      if (!press.triggered && deltaX * deltaX + deltaY * deltaY > this.longPressMoveTolerance * this.longPressMoveTolerance) {
        this.cancelLongPressAfterMovement()
      }
    },
    onLongPressPointerEnd(event) {
      if (!this._longPress || event.pointerId !== this._longPress.pointerId) return
      if (this._longPress.triggered) {
        this.finishTriggeredLongPress(event)
      } else if (event.type === "pointercancel") {
        // Browsers cancel the pointer sequence when native scrolling takes
        // control. A cancelled sequence is never a tap and must not open an
        // appointment, even if no large pointermove reached us beforehand.
        this.cancelLongPressAfterMovement()
      } else {
        this.finishTouchTap(event)
      }
    },
    onLongPressScroll() {
      if (this._longPress && !this._longPress.triggered) this.cancelLongPressAfterMovement()
    },
    cancelLongPressAfterMovement() {
      const press = this._longPress
      if (!press || press.triggered) return
      if (this._longPressTimer) clearTimeout(this._longPressTimer)
      this._longPressTimer = null

      // A finger move/scroll must never turn into an appointment click when
      // iPadOS emits its delayed compatibility click after the gesture. Keep a
      // short-lived record of the cancelled touch without preventing scrolling.
      const completedPress = {
        ...press,
        cancelledByMovement: true,
        suppressAnyCompatibilityClick: true,
        suppressClick: true,
        suppressContextMenu: true,
      }
      this._completedLongPress = completedPress
      this._longPress = null
      this.unbindLongPressGuards()
      this.releaseHoverPreviewSuppression()
      this.armTouchCompatibilityClickGuard(completedPress)
    },
    cancelLongPress(clearCompleted = false) {
      if (this._longPressTimer) clearTimeout(this._longPressTimer)
      this._longPressTimer = null
      this._longPress = null
      this.unbindLongPressGuards()
      if (clearCompleted) this.clearCompletedLongPress()
      this.releaseHoverPreviewSuppression()
    },
    deferTouchAction(callback) {
      if (this._deferredTouchActionTimer) clearTimeout(this._deferredTouchActionTimer)
      this._deferredTouchActionTimer = setTimeout(() => {
        this._deferredTouchActionTimer = null
        callback()
      }, 0)
    },
    finishTouchTap(event) {
      const press = this._longPress
      if (!press) return
      if (this._longPressTimer) clearTimeout(this._longPressTimer)
      this._longPressTimer = null
      this._completedLongPress = {
        ...press,
        suppressClick: true,
        suppressContextMenu: true,
      }
      this._longPress = null
      this.unbindLongPressGuards()
      this.releaseHoverPreviewSuppression()

      // Do not depend on Safari's delayed compatibility `click`: the tap is
      // completed from the real touch pointer sequence exactly once. The
      // compatibility click is swallowed by the capture handler below.
      this.consumeEvent(event)
      this.armTouchCompatibilityClickGuard(press)
      this.deferTouchAction(() => this.emitTouchTap(press))
    },
    finishTriggeredLongPress(event) {
      const press = this._longPress
      if (!press) return
      this._completedLongPress = {
        ...press,
        suppressClick: true,
        suppressContextMenu: !press.nativeContextConsumed,
      }
      this._longPress = null
      this._longPressTimer = null
      this.unbindLongPressGuards()
      this.releaseHoverPreviewSuppression()

      // The menu must never be inserted underneath a finger that is still
      // down. Otherwise iPadOS dispatches the release click to whichever menu
      // item appeared at that coordinate. Open only after pointerup/cancel and
      // after the corresponding compatibility click had a chance to run.
      this.consumeEvent(event)
      this.armTouchCompatibilityClickGuard(press)
      this.deferTouchAction(() => this.emitLongPressMenu(press))
    },
    finishCancelledLongPressCandidate() {
      const press = this._longPress
      if (!press) return
      if (this._longPressTimer) clearTimeout(this._longPressTimer)
      this._longPressTimer = null
      this._completedLongPress = {
        ...press,
        allowNativeContextFallback: true,
        suppressClick: false,
        suppressContextMenu: true,
      }
      this._longPress = null
      this.unbindLongPressGuards()
      this.releaseHoverPreviewSuppression()
    },
    clearCompletedLongPress() {
      this._completedLongPress = null
    },
    cleanupCompletedLongPress() {
      const completed = this._completedLongPress
      if (completed && !completed.suppressClick && !completed.suppressContextMenu) this.clearCompletedLongPress()
    },
    triggerLongPress(press = this._longPress) {
      if (!press || press.triggered) return false
      press.triggered = true
      if (press === this._longPress && this._longPressTimer) clearTimeout(this._longPressTimer)
      if (press === this._longPress) this._longPressTimer = null
      if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") navigator.vibrate(10)
      if (typeof window !== "undefined" && window.getSelection) {
        const selection = window.getSelection()
        if (selection && typeof selection.removeAllRanges === "function") selection.removeAllRanges()
      }
      return true
    },
    createPressSyntheticEvent(press) {
      return {
        clientX: press.clientX,
        clientY: press.clientY,
        calendarTargetClientY: press.startY,
        currentTarget: press.target,
        target: press.target,
        preventDefault() {},
        stopPropagation() {},
      }
    },
    emitTouchTap(press) {
      if (!press || !press.payload) return
      const syntheticEvent = this.createPressSyntheticEvent(press)
      if (press.payload.type === "item") {
        this.$emit("click-item", press.payload.block, press.payload.col)
      } else {
        this.$emit("click-empty", syntheticEvent, press.payload.col)
      }
    },
    emitLongPressMenu(press) {
      const syntheticEvent = this.createPressSyntheticEvent(press)
      if (press.payload.type === "item") {
        this.$emit("contextmenu-item", {
          event: syntheticEvent,
          block: press.payload.block,
          col: press.payload.col,
        })
      } else {
        this.$emit("contextmenu-empty", syntheticEvent, press.payload.col)
      }
    },
    isRelatedLongPressEvent(event, press) {
      if (!event || !press) return false
      const isCompatibilityMouse = event.pointerType === "mouse" && (!event.sourceCapabilities || event.sourceCapabilities.firesTouchEvents !== false)
      if (event.pointerType === "mouse" && !isCompatibilityMouse) return false
      if (event.pointerType && press.pointerType && event.pointerType !== press.pointerType && !isCompatibilityMouse) return false
      if (
        Number.isFinite(event.pointerId) &&
        event.pointerId > 0 &&
        Number.isFinite(press.pointerId) &&
        press.pointerId > 0 &&
        event.pointerId !== press.pointerId
      ) {
        return false
      }
      if (event.sourceCapabilities && event.sourceCapabilities.firesTouchEvents === false && !event.pointerType) return false
      if (Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) {
        const deltaX = event.clientX - press.clientX
        const deltaY = event.clientY - press.clientY
        const eventTolerance = Math.max(24, this.longPressMoveTolerance * 2)
        if (deltaX * deltaX + deltaY * deltaY > eventTolerance * eventTolerance) return false
      }
      return true
    },
    consumeEvent(event) {
      if (!event) return
      event.preventDefault()
      event.stopPropagation()
    },
    armTouchCompatibilityClickGuard(press) {
      if (!press || typeof window === "undefined") return
      this.clearTouchCompatibilityClickGuard()
      this._touchCompatibilityClickGuard = {
        press,
        until: Date.now() + 700,
      }
      window.addEventListener("click", this.onTouchCompatibilityWindowClick, true)
      window.addEventListener("pointerdown", this.onTouchCompatibilityNewPointerDown, true)
      this._touchCompatibilityClickGuardTimer = setTimeout(() => {
        this.clearTouchCompatibilityClickGuard()
        if (
          press.cancelledByMovement &&
          this._completedLongPress &&
          this._completedLongPress.cancelledByMovement &&
          this._completedLongPress.pointerId === press.pointerId
        ) {
          this.clearCompletedLongPress()
        }
      }, 700)
    },
    clearTouchCompatibilityClickGuard() {
      if (this._touchCompatibilityClickGuardTimer) clearTimeout(this._touchCompatibilityClickGuardTimer)
      this._touchCompatibilityClickGuardTimer = null
      this._touchCompatibilityClickGuard = null
      if (typeof window !== "undefined") {
        window.removeEventListener("click", this.onTouchCompatibilityWindowClick, true)
        window.removeEventListener("pointerdown", this.onTouchCompatibilityNewPointerDown, true)
      }
    },
    onTouchCompatibilityNewPointerDown(event) {
      if (!this._touchCompatibilityClickGuard) return
      // A deliberate follow-up tap always has its own pointerdown and must be
      // allowed immediately. Ignore only an old-style synthetic mouse event
      // generated from the touch sequence we have just completed.
      const touchGeneratedMouse =
        event && event.pointerType === "mouse" && event.sourceCapabilities && event.sourceCapabilities.firesTouchEvents === true
      if (!touchGeneratedMouse) this.clearTouchCompatibilityClickGuard()
    },
    onTouchCompatibilityWindowClick(event) {
      const guard = this._touchCompatibilityClickGuard
      if (!guard) return
      if (guard.until < Date.now()) {
        this.clearTouchCompatibilityClickGuard()
        return
      }

      const hasCoordinates = Number.isFinite(event.clientX) && Number.isFinite(event.clientY) && (event.clientX !== 0 || event.clientY !== 0)
      if (hasCoordinates && !guard.press.suppressAnyCompatibilityClick) {
        const deltaX = event.clientX - guard.press.clientX
        const deltaY = event.clientY - guard.press.clientY
        const tolerance = Math.max(36, this.longPressMoveTolerance * 2)
        if (deltaX * deltaX + deltaY * deltaY > tolerance * tolerance) {
          this.clearTouchCompatibilityClickGuard()
          return
        }
      }

      // Capture at window level: iPadOS can hit-test its delayed click against
      // DOM that was inserted after pointerup (for example a context-menu item),
      // so a handler on the original calendar block alone is not sufficient.
      if (this._completedLongPress) this._completedLongPress.suppressClick = false
      this.clearTouchCompatibilityClickGuard()
      this.consumeEvent(event)
      if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation()
      this.cleanupCompletedLongPress()
    },
    onTouchCompatibilityClickCapture(event) {
      if (!this.consumeLongPressClick(event)) return
      // `stopPropagation` is not enough when Vuetify's v-menu activator and
      // our block click live on the same element. Stop all remaining handlers
      // so a touch tap cannot first open the hover preview or open twice.
      if (typeof event.stopImmediatePropagation === "function") event.stopImmediatePropagation()
    },
    consumeLongPressClick(event) {
      const completed = this._completedLongPress
      if (!completed || !completed.suppressClick || !this.isRelatedLongPressEvent(event, completed)) return false
      completed.suppressClick = false
      this.consumeEvent(event)
      this.cleanupCompletedLongPress()
      return true
    },
    onBlockClick(event, block, col) {
      const pointerDragClick = this._suppressPointerBlockClick
      if (pointerDragClick && pointerDragClick.until >= Date.now() && pointerDragClick.blockId === String(block?._id || "")) {
        this._suppressPointerBlockClick = null
        this.consumeEvent(event)
        return
      }
      if (pointerDragClick && pointerDragClick.until < Date.now()) this._suppressPointerBlockClick = null
      if (this.consumeLongPressClick(event)) return
      this.$emit("click-item", block, col)
    },
    onEmptyClick(event, col) {
      if (this.consumeLongPressClick(event)) return
      this.$emit("click-empty", event, col)
    },
    shouldSuppressNativeContextMenu(event) {
      const active = this._longPress
      if (active && this.isRelatedLongPressEvent(event, active)) {
        this.triggerLongPress(active)
        active.nativeContextConsumed = true
        this.consumeEvent(event)
        return true
      }

      const completed = this._completedLongPress
      if (!completed || !completed.suppressContextMenu || !this.isRelatedLongPressEvent(event, completed)) return false
      if (!completed.triggered && completed.allowNativeContextFallback) {
        this.triggerLongPress(completed)
        completed.suppressClick = true
      }
      completed.suppressContextMenu = false
      this.consumeEvent(event)
      this.cleanupCompletedLongPress()
      return true
    },
    onEmptyContextMenu(event, col) {
      if (this.shouldSuppressNativeContextMenu(event)) return
      this.$emit("contextmenu-empty", event, col)
    },
    checkAbsenceCollapseState() {
      // If there are no items in this column, it's safe to expand
      if (!this.col || !this.col.items || this.col.items.length === 0) {
        this.isAbsencesCollapsed = false
        return
      }

      // Find the start hour of the calendar
      let calendarStartHour = 8 // Default fallback
      if (this.timeSlots && this.timeSlots.length > 0 && this.timeSlots[0].time) {
        const match = this.timeSlots[0].time.match(/^(\d+):/)
        if (match) {
          calendarStartHour = parseInt(match[1])
        }
      }

      // We check if there are any items that start within the first 3 hours of the calendar
      const blockingHourLimit = calendarStartHour + 3

      const hasBlockingItem = this.col.items.some((item) => {
        if (!item.start) return false
        const match = item.start.match(/^(\d+):/)
        if (match) {
          const itemStartHour = parseInt(match[1])
          return itemStartHour < blockingHourLimit
        }
        return false
      })

      // Collapse if there is a blocking item, otherwise expand
      this.isAbsencesCollapsed = hasBlockingItem
    },
    onContextMenu(event, block, col) {
      if (this.shouldSuppressNativeContextMenu(event) || !block || block.type === "system") return
      this.$emit("contextmenu-item", { event, block, col })
    },
    formatTrainerName(col) {
      if (!col) return ""
      if (col.id === "ma_info") return "MA Info"
      if (col.id === "no_show") return "Nicht gekommen"
      if (col.id === "unassigned") return "Nicht zugewiesen"
      const firstName = col.firstName || ""
      let lastName = col.info?.lastName || col.lastName || ""
      if (!lastName) {
        const fullName = col.info?.fullName || col.fullName || ""
        if (fullName) {
          const parts = fullName.trim().split(/\s+/)
          if (parts.length > 1) {
            lastName = parts.slice(1).join(" ")
          }
        }
      }
      if (this.showLastNameInitial && lastName) {
        return `${firstName} ${lastName.charAt(0).toUpperCase()}.`
      }
      return firstName
    },
    getAbsenceConfig(reason) {
      const r = reason === "Krankheit" ? "sick" : reason || "sick"
      return this.absenceConfigs[r] || this.absenceConfigs["sick"]
    },
    getAbsenceHeaderStyle(reason) {
      const config = this.getAbsenceConfig(reason)
      const color = config.color
      let borderBottom
      if (color.startsWith("var") || color.startsWith("color-mix")) {
        borderBottom = `1px solid color-mix(in srgb, ${color} 15%, transparent)`
      } else {
        borderBottom = `1px solid rgba(${config.bgColorRgb}, 0.15)`
      }
      return { borderBottom }
    },
    getSickBackgroundStyle(bg, isDarkMode) {
      const reason = bg.absenceReason || this.col.absenceReason || "sick"
      const config = this.getAbsenceConfig(reason)
      const color = config.color
      const baseBg = isDarkMode ? "#1e1e1e" : "#ffffff"

      let bgGrad, border
      if (color.startsWith("var") || color.startsWith("color-mix")) {
        bgGrad = `repeating-linear-gradient(45deg, color-mix(in srgb, ${color} 15%, ${baseBg}), color-mix(in srgb, ${color} 15%, ${baseBg}) 10px, color-mix(in srgb, ${color} 8%, ${baseBg}) 10px, color-mix(in srgb, ${color} 8%, ${baseBg}) 20px) !important`
        border = `1px solid color-mix(in srgb, ${color} 30%, ${baseBg}) !important`
      } else {
        const rgb = config.bgColorRgb
        bgGrad = `repeating-linear-gradient(45deg, color-mix(in srgb, rgb(${rgb}) 15%, ${baseBg}), color-mix(in srgb, rgb(${rgb}) 15%, ${baseBg}) 10px, color-mix(in srgb, rgb(${rgb}) 8%, ${baseBg}) 10px, color-mix(in srgb, rgb(${rgb}) 8%, ${baseBg}) 20px) !important`
        border = `1px solid rgba(${rgb}, 0.3) !important`
      }

      const isPartial = bg.sickPercentage !== undefined && bg.sickPercentage < 100

      return {
        top: bg.top + "px",
        height: bg.height + "px",
        background: bgGrad,
        border: isPartial ? `2px dashed rgba(244, 67, 54, 0.9) !important` : border,
        borderLeft: isPartial ? `4px solid #f44336 !important` : `4px dashed ${color} !important`,
        zIndex: 3,
        pointerEvents: isPartial ? "auto" : "none",
        borderRadius: "6px",
      }
    },
    getSickWarningBannerStyle(isDarkMode) {
      const reason = this.col.absenceReason || "sick"
      const config = this.getAbsenceConfig(reason)
      const color = isDarkMode ? config.color : config.lightColor

      let background, borderBottom
      if (color.startsWith("var") || color.startsWith("color-mix")) {
        background = isDarkMode
          ? `color-mix(in srgb, ${color} 12%, transparent) !important`
          : `color-mix(in srgb, ${color} 8%, transparent) !important`
        borderBottom = isDarkMode
          ? `2px solid color-mix(in srgb, ${color} 30%, transparent) !important`
          : `2px solid color-mix(in srgb, ${color} 20%, transparent) !important`
      } else {
        const rgb = config.bgColorRgb
        background = isDarkMode ? `rgba(${rgb}, 0.12) !important` : `rgba(${rgb}, 0.08) !important`
        borderBottom = isDarkMode ? `2px solid rgba(${rgb}, 0.3) !important` : `2px solid rgba(${rgb}, 0.2) !important`
      }

      return {
        background,
        borderBottom,
        color: `${color} !important`,
      }
    },
    getSickEmpCardStyle(emp) {
      const reason = emp.absenceReason || "sick"
      const config = this.getAbsenceConfig(reason)
      const color = config.color
      const baseBg = this.isDarkMode ? "#1e1e1e" : "#ffffff"

      const background = `color-mix(in srgb, ${color} 10%, ${baseBg})`

      let border
      if (color.startsWith("var") || color.startsWith("color-mix")) {
        border = `1px solid color-mix(in srgb, ${color} 25%, transparent)`
      } else {
        const rgb = config.bgColorRgb
        border = `1px solid rgba(${rgb}, 0.25)`
      }

      return {
        background,
        border,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        zIndex: 9,
      }
    },
    formatTimes(times) {
      if (!times) return "Keine Zeit"
      if (typeof times === "string") {
        if (times.trim().startsWith("[") && times.trim().endsWith("]")) {
          try {
            const parsed = JSON.parse(times)
            if (Array.isArray(parsed)) {
              return parsed
                .filter((t) => t.type !== "Pause")
                .map((t) => `${t.start} - ${t.end}`)
                .join(", ")
            }
          } catch (e) {
            // Ignore error
          }
        }
        return times
      }
      if (Array.isArray(times)) {
        return times
          .filter((t) => t.type !== "Pause")
          .map((t) => `${t.start} - ${t.end}`)
          .join(", ")
      }
      return times
    },
    hasRealCustomerPicture(customer) {
      const cust = customer || this.hoveredCustomerDetails?.details?.rawDetails
      if (!cust) return false
      const bild = cust.bild || (cust.user ? cust.user.bild : null)
      const tacImage = cust.tac_image || (cust.user ? cust.user.tac_image : null)

      if (bild) {
        const b = String(bild)
        if (b.startsWith("data:image") || b.length > 200) return true
        const bLower = b.toLowerCase()
        if (bLower.includes("placeholder") || bLower.includes("default") || bLower.includes("150x150") || bLower.includes("150")) return false
        return true
      }
      if (tacImage) {
        const t = String(tacImage)
        if (t.startsWith("data:image") || t.length > 200) return true
        const tLower = t.toLowerCase()
        if (tLower.includes("placeholder") || tLower.includes("default") || tLower.includes("150x150") || tLower.includes("150")) return false
        return true
      }
      return false
    },
    getCustomerInitials(customer, noAccountData) {
      let first = ""
      let last = ""
      if (customer) {
        first = customer.vorname || ""
        last = customer.nachname || ""
      } else if (noAccountData) {
        first = noAccountData.firstName || ""
        last = noAccountData.lastName || ""
      }
      const fChar = first.trim().charAt(0).toUpperCase()
      const lChar = last.trim().charAt(0).toUpperCase()
      return `${fChar}${lChar}` || "?"
    },
    copyToClipboard(text, label) {
      if (!navigator.clipboard) return
      navigator.clipboard.writeText(text).then(() => {
        this.$toast && this.$toast.success(`${label} kopiert!`)
      })
    },
    getCustomerPicture(customer) {
      const cust = customer || this.hoveredCustomerDetails?.details?.rawDetails
      if (!cust) return "/img/icons/user_placeholder.png"
      const bild = cust.bild || (cust.user ? cust.user.bild : null)
      const tacImage = cust.tac_image || (cust.user ? cust.user.tac_image : null)
      if (bild) {
        if (bild.includes("uploads")) return "/img/profilbilder/" + bild
        return "data:image/jpeg;base64," + bild
      }
      if (tacImage) {
        if (tacImage.startsWith("data:image")) {
          return tacImage
        }
        return "data:image/jpeg;base64," + tacImage
      }
      return "/img/icons/user_placeholder.png"
    },
    getApptDetails(block) {
      let title = block.title || "Termin"
      let name = block.subtitle || ""
      let info = block.description || ""

      // Wir versuchen, den Kundennamen zu ermitteln
      let detectedName = ""
      if (block.rawData) {
        if (block.rawData.customer) {
          detectedName = `${block.rawData.customer.vorname} ${block.rawData.customer.nachname}${
            block.rawData.customer2 ? ` + ${block.rawData.customer2.vorname}` : ""
          }`.trim()
        } else if (block.rawData.noAccountData && block.rawData.noAccountData.firstName) {
          detectedName = `${block.rawData.noAccountData.firstName} ${block.rawData.noAccountData.lastName}`.trim()
        } else if (block.rawData.guestData && block.rawData.guestData.firstName) {
          detectedName = `${block.rawData.guestData.firstName} ${block.rawData.guestData.lastName}`.trim()
        } else if (block.rawData.rawData && block.rawData.rawData.guestData && block.rawData.rawData.guestData.firstName) {
          detectedName = `${block.rawData.rawData.guestData.firstName} ${block.rawData.rawData.guestData.lastName}`.trim()
        }
      }

      // Falls der Name ermittelt werden konnte:
      if (detectedName) {
        name = detectedName
        // Wenn der Titel denselben Namen enthält wie der ermittelte Kundenname (z.B. manuell so eingegeben),
        // dann nehmen wir die Beschreibung/Untertitel als den Termin-Titel (z.B. "Einführungstraining")
        if (title.toLowerCase().includes(detectedName.toLowerCase()) || detectedName.toLowerCase().includes(title.toLowerCase())) {
          title = block.subtitle && block.subtitle !== detectedName ? block.subtitle : block.description || "Termin"
        }
      }

      // Smart Swap: If name looks like a training type/template and title looks like a customer name, swap them!
      // This ensures that template title is always Title (largest) and customer name is Name (below it).
      const nameLower = name.toLowerCase()
      const titleLower = title.toLowerCase()
      const trainingTerms = [
        "einführung",
        "training",
        "probetraining",
        "beratung",
        "kontroll",
        "vertrag",
        "mescan",
        "pelvix",
        "anamnese",
        "checkup",
        "milon",
        "nutrition",
        "ernährung",
        "upgrade",
        "termin",
      ]
      const isNameTraining = trainingTerms.some((term) => nameLower.includes(term))
      const isTitleTraining = trainingTerms.some((term) => titleLower.includes(term))

      if (isNameTraining && !isTitleTraining) {
        const temp = title
        title = name
        name = temp
      }

      // Info: Wenn info dem Titel oder Name entspricht, leeren wir sie, um Dopplungen zu vermeiden
      if (info === title || info === name) {
        info = ""
      }

      return { title, name, info }
    },
    startResize(e, block, edge = "bottom") {
      if (!this.$moment || !block.start) return

      let startDuration = 60
      if (block.end) {
        const sm = this.$moment(block.start, "HH:mm")
        const em = this.$moment(block.end, "HH:mm")
        startDuration = em.diff(sm, "minutes") || 60
      }

      this.resizeData = {
        block: block,
        pointerId: e.pointerId,
        startY: e.clientY,
        edge: edge,
        startDuration: startDuration,
        top: block.top,
        height: block.height,
        isPause: block.type === "pause",
        parentShiftId: block.parentShiftId || block.rawData?.parentShiftId,
        timeIndex: block.timeIndex,
        newStartStr: block.start,
        newEndStr: block.end || this.$moment(block.start, "HH:mm").add(60, "minutes").format("HH:mm"),
        newDuration: startDuration,
      }
      document.addEventListener("pointermove", this.onResizeMove)
      document.addEventListener("pointerup", this.onResizeEnd)
      document.addEventListener("pointercancel", this.onResizeEnd)
    },
    startDesktopResize(event, block, edge = "bottom") {
      // Media-Queries sind auf Windows-Hybridgeräten unzuverlässig. Der echte
      // Pointer des aktuellen Kontakts ist die verlässliche Quelle: Trackpad
      // und Maus melden `mouse`, Touch/Stift bleiben beim Langdruck-Menü.
      if (event && event.pointerType && event.pointerType !== "mouse") return
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }
      this._activeBlockPointerType = "mouse"
      this.startResize(event, block, edge)
    },
    onResizeMove(e) {
      if (!this.resizeData || !this.$moment) return
      if (this.resizeData.pointerId != null && e.pointerId !== this.resizeData.pointerId) return
      e.preventDefault()
      const deltaY = e.clientY - this.resizeData.startY
      const deltaMins = Math.round(((deltaY / this.hourHeight) * 60) / 15) * 15

      if (this.resizeData.edge === "top") {
        const newStartMoment = this.$moment(this.resizeData.block.start, "HH:mm").add(deltaMins, "minutes")
        const endMoment = this.$moment(this.resizeData.block.end || this.resizeData.newEndStr, "HH:mm")
        const diff = endMoment.diff(newStartMoment, "minutes")
        if (diff >= 15) {
          this.resizeData.newStartStr = newStartMoment.format("HH:mm")
          this.resizeData.top = this.resizeData.block.top + (deltaMins / 60) * this.hourHeight
          this.resizeData.height = (diff / 60) * this.hourHeight
          this.resizeData.newDuration = diff
        }
      } else {
        const newDuration = Math.max(15, this.resizeData.startDuration + deltaMins)
        this.resizeData.height = (newDuration / 60) * this.hourHeight
        this.resizeData.newEndStr = this.$moment(this.resizeData.block.start, "HH:mm").add(newDuration, "minutes").format("HH:mm")
        this.resizeData.newDuration = newDuration
      }
    },
    onResizeEnd(e) {
      if (this.resizeData && e && this.resizeData.pointerId != null && e.pointerId !== this.resizeData.pointerId) return
      const cancelled = e && e.type === "pointercancel"
      document.removeEventListener("pointermove", this.onResizeMove)
      document.removeEventListener("pointerup", this.onResizeEnd)
      document.removeEventListener("pointercancel", this.onResizeEnd)
      if (!this.resizeData) return

      const { block, newStartStr, newEndStr, newDuration, startDuration } = this.resizeData

      if (!cancelled && newDuration && newDuration !== startDuration) {
        this.$emit("resize-block", {
          block,
          col: this.col,
          newDuration,
          newStartStr,
          newEndStr,
        })
      }
      this.resizeData = null
    },
    startShiftResize(e, bg, edge) {
      if (!this.$moment || !bg.start || !bg.end) return

      this.shiftResizeData = {
        bg: bg,
        pointerId: e.pointerId,
        startY: e.clientY,
        edge: edge,
        start: bg.start,
        end: bg.end,
        top: bg.top,
        height: bg.height,
        newStartStr: bg.start,
        newEndStr: bg.end,
      }
      document.addEventListener("pointermove", this.onShiftResizeMove)
      document.addEventListener("pointerup", this.onShiftResizeEnd)
      document.addEventListener("pointercancel", this.onShiftResizeEnd)
    },
    startDesktopShiftResize(event, bg, edge) {
      if (event && event.pointerType && event.pointerType !== "mouse") return
      if (event) {
        event.preventDefault()
        event.stopPropagation()
      }
      this._activeBlockPointerType = "mouse"
      this.startShiftResize(event, bg, edge)
    },
    onShiftResizeMove(e) {
      if (!this.shiftResizeData || !this.$moment) return
      if (this.shiftResizeData.pointerId != null && e.pointerId !== this.shiftResizeData.pointerId) return
      e.preventDefault()
      const deltaY = e.clientY - this.shiftResizeData.startY
      const deltaMins = Math.round(((deltaY / this.hourHeight) * 60) / 15) * 15

      if (this.shiftResizeData.edge === "top") {
        const startMoment = this.$moment(this.shiftResizeData.start, "HH:mm").add(deltaMins, "minutes")
        const endMoment = this.$moment(this.shiftResizeData.end, "HH:mm")

        // Ensure start is at least 30 mins before end
        if (endMoment.diff(startMoment, "minutes") >= 30) {
          this.shiftResizeData.newStartStr = startMoment.format("HH:mm")
          this.shiftResizeData.top = this.shiftResizeData.bg.top + (deltaMins / 60) * this.hourHeight
          this.shiftResizeData.height = this.shiftResizeData.bg.height - (deltaMins / 60) * this.hourHeight
        }
      } else {
        const startMoment = this.$moment(this.shiftResizeData.start, "HH:mm")
        const endMoment = this.$moment(this.shiftResizeData.end, "HH:mm").add(deltaMins, "minutes")

        // Ensure end is at least 30 mins after start
        if (endMoment.diff(startMoment, "minutes") >= 30) {
          this.shiftResizeData.newEndStr = endMoment.format("HH:mm")
          this.shiftResizeData.height = this.shiftResizeData.bg.height + (deltaMins / 60) * this.hourHeight
        }
      }
    },
    onShiftResizeEnd(e) {
      if (this.shiftResizeData && e && this.shiftResizeData.pointerId != null && e.pointerId !== this.shiftResizeData.pointerId) return
      const cancelled = e && e.type === "pointercancel"
      document.removeEventListener("pointermove", this.onShiftResizeMove)
      document.removeEventListener("pointerup", this.onShiftResizeEnd)
      document.removeEventListener("pointercancel", this.onShiftResizeEnd)
      if (!this.shiftResizeData) return

      if (
        !cancelled &&
        (this.shiftResizeData.newStartStr !== this.shiftResizeData.start || this.shiftResizeData.newEndStr !== this.shiftResizeData.end)
      ) {
        this.$emit("resize-shift", {
          col: this.col,
          shiftId: this.shiftResizeData.bg.shiftId,
          originalStart: this.shiftResizeData.start,
          originalEnd: this.shiftResizeData.end,
          newStart: this.shiftResizeData.newStartStr,
          newEnd: this.shiftResizeData.newEndStr,
          times: this.shiftResizeData.bg.times,
        })
      }
      this.shiftResizeData = null
    },
    onDragOver(e) {
      if (e.dataTransfer) {
        const types = Array.from(e.dataTransfer.types || []).map((t) => t.toLowerCase())
        const isBlock = types.includes("blockmove")
        e.dataTransfer.dropEffect = isBlock ? (e.ctrlKey ? "copy" : "move") : "copy"
      }

      // Native dragover-Events können auf Touch-/Hybrid-Geräten mehrere
      // hundert Mal pro Sekunde feuern. Bereits hier drosseln, bevor eine
      // synchrone Layoutmessung oder ein Vue-Update ausgelöst wird.
      const now = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now()
      if (this._lastColumnDragOverAt && now - this._lastColumnDragOverAt < 40) return
      this._lastColumnDragOverAt = now

      const laneBody = this.$refs.laneBody
      if (!laneBody) {
        return
      }
      const rect = laneBody.getBoundingClientRect()
      const relativeY = e.clientY - rect.top
      this.$emit("drag-over", { event: e, colId: this.col.id, relativeY })
    },
    onDrop(e) {
      this._lastColumnDragOverAt = 0
      this.$emit("drop", { event: e, col: this.col })
    },
    getBlockTimeStatus(block) {
      if (!block.isAppt || !block.start || !block.end) return ""
      if (!this.$moment) return ""

      // nowTick in der Berechnung verwenden → Vue erkennt die Abhängigkeit und re-rendert
      const _tick = this.nowTick
      const now = this.$moment()
      const calDate = this.$moment(this.currentDate || now)

      // Not today = everything is past or future
      if (calDate.isBefore(now, "day")) return "past"
      if (calDate.isAfter(now, "day")) return "future"

      // It is today. Parse time.
      const startMoment = this.$moment(block.start, "HH:mm")
      const endMoment = this.$moment(block.end, "HH:mm")

      if (now.isAfter(endMoment)) return "past"
      if (now.isBetween(startMoment, endMoment, "minute", "[]")) return "live"

      const diffMins = startMoment.diff(now, "minutes")
      if (diffMins > 0 && diffMins <= 30) return "upcoming"

      return "future"
    },
    getMinutesUntil(timeStr) {
      if (!this.$moment) return 0
      const _tick = this.nowTick // Reaktive Abhängigkeit
      const now = this.$moment()
      const startMoment = this.$moment(timeStr, "HH:mm")
      return startMoment.diff(now, "minutes")
    },
    onBlockMouseEnter(block) {
      if (this.hasHoverSupport && block.isAppt && block.rawData?.customer?._id) {
        this.$emit("hover-block", block)
      }
    },
    showTrainingFlow(block) {
      const type = (block.rawData?.specialType || "").toLowerCase()
      const trainingTypes = ["trial", "intro", "followup", "check", "milon", "nutrition", "consultation", "trainingplan", "workout"]
      return trainingTypes.includes(type)
    },
    membershipCardStyle(contract) {
      const dark = this.isDarkMode
      if (!contract) {
        return {
          background: dark ? "#262626 !important" : "#f8f9fa !important",
          color: "var(--text-muted) !important",
          border: `1px dashed ${dark ? "#444" : "#ccc"} !important`,
          boxShadow: "none !important",
        }
      }
      const isActive = contract.CONTRACTSTATE === "Laufend"

      if (isActive) {
        return {
          background: dark ? "rgba(76, 175, 80, 0.12) !important" : "rgba(76, 175, 80, 0.08) !important",
          color: dark ? "#81c784 !important" : "#2e7d32 !important",
          border: `1px solid ${dark ? "rgba(76, 175, 80, 0.3)" : "rgba(76, 175, 80, 0.2)"} !important`,
          boxShadow: "none !important",
        }
      } else {
        return {
          background: dark ? "#2d2d2d !important" : "#e9ecef !important",
          color: "var(--text-main) !important",
          border: `1px solid ${dark ? "#444" : "#ddd"} !important`,
          boxShadow: "none !important",
        }
      }
    },
    formatDate(date) {
      if (!date) return ""
      return this.$moment(date).format("DD.MM.YYYY")
    },
    formatDateDayMonth(date) {
      if (!date) return ""
      return this.$moment(date).format("DD.MM.")
    },
    onBlockDragStart(e, block) {
      if (this.usesPointerDragFallback || this._activeBlockPointerType === "touch" || this._activeBlockPointerType === "pen") {
        // Falls ein untergeordnetes Bild trotz `draggable=false` einen nativen
        // Drag anstösst, muss auch dieser Weg auf Hybrid-Geräten aus bleiben.
        // Sonst erzeugt Chromium wieder das sofortige `pointercancel`.
        e.preventDefault()
        return
      }
      if (!e.dataTransfer) {
        return
      }
      // Absichtlich nicht `draggingBlockId` reaktiv setzen: Eine neue Klasse
      // würde zugleich den umgebenden v-menu-Status ändern und den Drag-Quell-
      // DOM patchen. Das beendet den nativen Drag in Chromium auf manchen
      // Touch-Laptops, obwohl er mit Maus/Trackpad gestartet wurde.
      this._nativeDraggingBlockId = block._id
      // Block-Daten in dataTransfer speichern
      let duration = block.rawData?.duration || 60
      if (block.start && block.end && this.$moment) {
        const sm = this.$moment(block.start, "HH:mm")
        const em = this.$moment(block.end, "HH:mm")
        duration = em.diff(sm, "minutes") || duration
      }

      let dragOffsetY = 0
      if (e.currentTarget && e.currentTarget.getBoundingClientRect) {
        const rect = e.currentTarget.getBoundingClientRect()
        dragOffsetY = e.clientY - rect.top
      }

      const moveData = {
        _id: block._id,
        isTask: !!block.isTask,
        isAppt: !!block.isAppt,
        isPause: block.type === "pause",
        title: block.title,
        start: block.start,
        end: block.end,
        duration: duration,
        sourceColId: this.col.id,
        dragOffsetY: dragOffsetY,
        parentShiftId: block.parentShiftId || block.rawData?.parentShiftId,
        timeIndex: block.timeIndex,
        rawData: block.rawData || null,
        type: block.type,
      }
      // Das vollständige rawData bleibt lokal im Parent. Es kann Kundenbilder
      // als Base64 enthalten und würde den Browser beim synchronen Serialisieren
      // in dataTransfer kurzzeitig oder komplett blockieren.
      const transferData = { ...moveData }
      delete transferData.rawData
      const serializedTransferData = JSON.stringify(transferData)
      e.dataTransfer.setData("blockMove", serializedTransferData)
      // Chromium/Windows hält einen nativen In-App-Drag zuverlässiger aktiv,
      // wenn neben dem eigenen MIME-Typ auch ein Standardtyp vorhanden ist.
      // Ausgelesen wird weiterhin ausschließlich `blockMove`.
      e.dataTransfer.setData("text/plain", "work-calendar-block")
      e.dataTransfer.effectAllowed = "copyMove"
      // Für handleDragOver Ghost-Höhe
      this.$emit("block-drag-start", moveData)
    },
    onBlockDragEnd() {
      this.draggingBlockId = null
      this._nativeDraggingBlockId = null
      this._lastColumnDragOverAt = 0
      this._activeBlockPointerType = null
      this.$emit("block-drag-end")
    },
  },
}
</script>

<style scoped>
.sick-warning-banner {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(244, 67, 54, 0.95) !important;
  border-bottom: 2px solid rgba(244, 67, 54, 0.3) !important;
  color: #ff5252 !important;
  padding: 6px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: center;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(10px);
}
.holiday-work-warning-banner {
  flex-direction: row;
  min-height: 32px;
  padding: 6px 10px;
}
.sick-warning-text {
  font-weight: 800;
  letter-spacing: 0.3px;
  color: #ff5252;
}
.theme--light .sick-warning-banner {
  background: rgba(244, 67, 54, 0.92) !important;
  border-bottom: 2px solid rgba(244, 67, 54, 0.2) !important;
  color: #d32f2f !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}
.theme--light .sick-warning-text {
  color: #d32f2f;
}
.sick-employees-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 95; /* above normal blocks (z-index: 5) and hovered blocks (z-index: 90) but below sticky header (z-index: 100) */
  pointer-events: auto;
}
.sick-emp-card {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3) !important;
  transition: all 0.2s ease-in-out;
}
.sick-emp-card-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
}
.sick-emp-card-identity {
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.sick-emp-card-avatar {
  margin: 0 !important;
}
.sick-emp-card-copy {
  min-width: 0;
}
.sick-emp-card-name {
  overflow: hidden;
  color: var(--text-main);
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sick-emp-card-label {
  display: block;
  overflow: hidden;
  margin-top: 2px;
  font-size: 0.65rem !important;
  letter-spacing: 0.5px;
  line-height: 1.2 !important;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sick-emp-card-action,
.sick-emp-card-toggle {
  width: 100%;
}
.sick-emp-card-toggle {
  letter-spacing: 0.2px;
}
.sick-employee-column {
  background-color: rgba(244, 67, 54, 0.03) !important;
  border-left: 2px dashed rgba(244, 67, 54, 0.25) !important;
  border-right: 2px dashed rgba(244, 67, 54, 0.25) !important;
}
.sick-employee-column .wc-col-header {
  background: rgba(244, 67, 54, 0.12) !important;
  border-bottom: 2px solid rgba(244, 67, 54, 0.3) !important;
}
.sick-emp-card .sick-emp-shifts {
  display: none;
}
.sick-emp-card:hover .sick-emp-shifts {
  display: block;
}
.border-error-light {
  border: 1px solid rgba(255, 82, 82, 0.3) !important;
}
.border-red {
  border: 2px solid #ff5252 !important;
}

.employee-column {
  border-right: 1px solid var(--border-light);
  background: var(--bg-main);
  transition: background 0.2s;
  height: max-content;
}

.employee-column.wc-col-drag-valid {
  background-color: rgba(76, 175, 80, 0.06) !important;
  box-shadow: inset 0 0 12px rgba(76, 175, 80, 0.25) !important;
}

.employee-column.wc-col-drag-invalid {
  background-image: repeating-linear-gradient(
    45deg,
    rgba(244, 67, 54, 0.04),
    rgba(244, 67, 54, 0.04) 10px,
    rgba(244, 67, 54, 0.08) 10px,
    rgba(244, 67, 54, 0.08) 20px
  ) !important;
  background-color: rgba(244, 67, 54, 0.02) !important;
  box-shadow: inset 0 0 12px rgba(244, 67, 54, 0.25) !important;
}

/* Helper Columns specific styles */
.employee-column.helper-column-ma {
  background: rgba(33, 150, 243, 0.05); /* Subtle blue */
  border-right: 1px solid rgba(33, 150, 243, 0.1);
}
.employee-column.helper-column-no-show {
  background: rgba(244, 67, 54, 0.05); /* Subtle red */
  border-right: 1px solid rgba(244, 67, 54, 0.1);
}
.employee-column.helper-column-unassigned {
  background: rgba(255, 152, 0, 0.05); /* Subtle orange */
  border-right: 1px solid rgba(255, 152, 0, 0.1);
}
.theme--light .employee-column.helper-column-ma {
  background: rgba(33, 150, 243, 0.08);
}
.theme--light .employee-column.helper-column-no-show {
  background: rgba(244, 67, 54, 0.08);
}
.theme--light .employee-column.helper-column-unassigned {
  background: rgba(255, 152, 0, 0.08);
}

.wc-col-header {
  position: -webkit-sticky; /* WICHTIG FoR IPAD */
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: var(--bg-header);
  border-bottom: 1px solid var(--border-medium);
  z-index: 100; /* über Blöcken (5) und der Zeitlinie (96), aber unter Overlays/Sidebar */
  height: 80px; /* Hlt den Header millimetergenau auf der Hhe des linken Offsets */
  box-sizing: border-box;
}
.wc-col-header-left {
  flex-shrink: 0;
}
.wc-col-header-info {
  flex-grow: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1px;
}
.wc-col-header-right {
  flex-shrink: 0;
  align-self: flex-start;
  margin-top: -2px;
  margin-right: -4px;
}
.wc-col-avatar {
  border: 2px solid var(--border-strong) !important;
  flex-shrink: 0;
}
.wc-col-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}
.wc-col-time-row {
  display: flex;
  align-items: center;
  overflow: hidden;
}
.wc-col-time {
  font-size: 0.72rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.1;
}
.wc-col-badges-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  flex-wrap: wrap;
}

/* Column Badges */
.wc-col-badge {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 0.65rem;
  font-weight: 700;
  color: var(--text-muted);
  background: var(--bg-panel);
  padding: 2px 5px;
  border-radius: 6px;
  transition: all 0.2s ease;
}
.wc-col-badge .v-icon {
  color: var(--icon-muted) !important;
}
.wc-col-badge-task {
  color: rgba(171, 71, 188, 0.9);
}
.wc-col-badge-note {
  color: #42a5f5 !important;
  background: rgba(66, 165, 245, 0.12) !important;
  border: 1px solid rgba(66, 165, 245, 0.35) !important;
  box-shadow: 0 0 6px rgba(66, 165, 245, 0.15);
  animation: pulse-note 2s infinite ease-in-out;
}
.wc-col-badge-note:hover {
  background: rgba(66, 165, 245, 0.22) !important;
  border-color: rgba(66, 165, 245, 0.6) !important;
  box-shadow: 0 0 10px rgba(66, 165, 245, 0.35);
}
.wc-col-badge-note .v-icon {
  color: #42a5f5 !important;
}

@keyframes pulse-note {
  0% {
    box-shadow: 0 0 4px rgba(66, 165, 245, 0.15);
  }
  50% {
    box-shadow: 0 0 8px rgba(66, 165, 245, 0.35);
  }
  100% {
    box-shadow: 0 0 4px rgba(66, 165, 245, 0.15);
  }
}

.relative {
  position: relative;
}

/* ===== GRID ===== */
.grid-line {
  position: absolute;
  left: 0;
  right: 0;
  height: 1px;
  background: var(--bg-time-line);
  pointer-events: none;
}

.wc-thick-lines .grid-line {
  height: 1.2px !important;
  background-color: rgba(0, 0, 0, 0.08) !important;
}
.theme--dark .wc-thick-lines .grid-line {
  background-color: rgba(255, 255, 255, 0.12) !important;
}
.wc-thick-lines.employee-column {
  border-right: 1.2px solid rgba(0, 0, 0, 0.08) !important;
}
.theme--dark .wc-thick-lines.employee-column {
  border-right: 1.2px solid rgba(255, 255, 255, 0.12) !important;
}

/* ===== SHIFT BACKGROUND ===== */
.shift-background {
  position: absolute;
  left: 3px;
  right: 3px;
  z-index: 3;
  pointer-events: none;
  border-radius: 6px;
  /* Optionale weiche Bottom-Border für mehr Struktur */
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02) inset;
}

.shift-resize-handle {
  position: absolute;
  left: 0;
  right: 0;
  height: 8px;
  cursor: ns-resize;
  pointer-events: auto; /* Wichtig, da Parent pointer-events: none hat! */
  z-index: 10;
  transition: background-color 0.2s;
}
.shift-resize-handle.top {
  top: 0;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
}
.shift-resize-handle.bottom {
  bottom: 0;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}
.shift-resize-handle:hover {
  background-color: rgba(255, 152, 0, 0.4);
}

/* ===== BLOCKS ===== */
.wc-block {
  position: absolute;
  left: 6px;
  right: 6px;
  z-index: 5;
  cursor: pointer;
  background: var(--bg-block);
  border-radius: 10px;
  overflow: hidden;
  transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.25s cubic-bezier(0.34, 1.56, 0.64, 1), z-index 0s;
  -webkit-tap-highlight-color: transparent; /* Kein blaues Highlight auf Mobile */
}
/* Hover-Effekte NUR auf Geräten mit Maus/Trackpad */
@media (any-hover: hover) {
  .wc-block:not(.item-pause):hover {
    z-index: 90 !important; /* ensure it is above everything else when hovered */
    opacity: 1 !important;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2) !important;
  }
}
/* Touch-Feedback auf Mobile: subtiler Active-State */
@media (any-hover: none) {
  .wc-block:active {
    transform: scale(0.98);
    opacity: 0.85;
  }
}
.wc-block-inner {
  position: relative;
  z-index: 1;
}

/* Block Text */
.wc-block-time {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--text-muted);
  white-space: nowrap;
}
.wc-block-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-main);
  line-height: 1.3;
}
.wc-block-subtitle {
  font-size: 0.7rem;
  color: var(--text-muted);
}
.wc-block-small .wc-block-title {
  font-size: 0.75rem;
}
.wc-block-small .wc-block-time {
  font-size: 0.65rem;
}

/* Hover Actions — nur auf Desktop sichtbar */
.wc-block-actions {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 2;
}
@media (any-hover: hover) {
  .wc-block:hover .wc-block-actions {
    opacity: 1;
  }
}
.wc-action-btn {
  width: 24px !important;
  height: 24px !important;
  background: var(--bg-panel-dark) !important;
  color: var(--text-main) !important;
}

/* ===== SPECIAL BLOCK TYPES ===== */
.item-pause {
  border-left-color: #ff9800 !important;
}
.theme--light .item-pause {
  background: repeating-linear-gradient(
    45deg,
    rgba(255, 152, 0, 0.16),
    rgba(255, 152, 0, 0.16) 10px,
    rgba(0, 0, 0, 0.16) 10px,
    rgba(0, 0, 0, 0.16) 20px
  ) !important;
  border: 1px solid rgba(255, 152, 0, 0.5) !important;
  border-left: 4px solid #ff9800 !important;
}
.theme--dark .item-pause {
  background: repeating-linear-gradient(
    45deg,
    rgba(255, 152, 0, 0.25),
    rgba(255, 152, 0, 0.25) 10px,
    rgba(255, 255, 255, 0.25) 10px,
    rgba(255, 255, 255, 0.25) 20px
  ) !important;
  border: 1px solid rgba(255, 152, 0, 0.6) !important;
  border-left: 4px solid #ff9800 !important;
}

.item-system {
  pointer-events: none;
  border-left: 4px solid #d32f2f !important;
}
.theme--light .item-system {
  background: repeating-linear-gradient(
    45deg,
    rgba(183, 28, 28, 0.15),
    rgba(183, 28, 28, 0.15) 10px,
    rgba(183, 28, 28, 0.08) 10px,
    rgba(183, 28, 28, 0.08) 20px
  ) !important;
  border: 1px solid rgba(183, 28, 28, 0.4) !important;
  border-left: 4px solid #d32f2f !important;
}
.theme--dark .item-system {
  background: repeating-linear-gradient(
    45deg,
    rgba(211, 47, 47, 0.22),
    rgba(211, 47, 47, 0.22) 10px,
    rgba(211, 47, 47, 0.12) 10px,
    rgba(211, 47, 47, 0.12) 20px
  ) !important;
  border: 1px solid rgba(244, 67, 54, 0.5) !important;
  border-left: 4px solid #d32f2f !important;
}

.shift-background.shift-sick {
  background: repeating-linear-gradient(
    45deg,
    rgba(244, 67, 54, 0.15),
    rgba(244, 67, 54, 0.15) 10px,
    rgba(244, 67, 54, 0.08) 10px,
    rgba(244, 67, 54, 0.08) 20px
  ) !important;
}
.sick-watermark {
  font-size: 1.1rem;
  letter-spacing: 2px;
  color: rgba(244, 67, 54, 0.65);
  text-transform: uppercase;
  pointer-events: none;
  user-select: none;
}
.wc-block.wc-block-sick-parent {
  border-left-color: #ff5252 !important;
  box-shadow: 0 0 0 2px rgba(244, 67, 54, 0.4), 0 4px 15px rgba(244, 67, 54, 0.25) !important;
}

/* ===== STATUSES ===== */
.status-cancelled {
  opacity: 0.4;
  filter: grayscale(100%);
}
.status-checkedin {
  box-shadow: inset 0 0 0 1px rgba(0, 230, 118, 0.2);
}
.status-noshow {
  box-shadow: inset 0 0 0 1px rgba(255, 82, 82, 0.2);
}

/* SMART STATUSES */
.wc-block-past {
  opacity: 0.75;
  filter: grayscale(30%);
}

@keyframes upcoming-pulse {
  0%,
  100% {
    border-left-color: rgba(255, 193, 7, 0.8);
    opacity: 1;
  }
  50% {
    border-left-color: var(--border-strong);
    opacity: 0.85;
  }
}
.wc-block-upcoming {
  animation: upcoming-pulse 2.5s ease-in-out infinite;
  border-left-color: rgba(255, 193, 7, 0.8);
}

.wc-block-live {
  border-left-color: #4caf50 !important;
  border-left-width: 5px;
}

.wc-block-dragging {
  opacity: 0.3 !important;
  filter: grayscale(50%);
  transition: opacity 0.15s;
}

/* Auf Hybrid-Geräten darf auch kein verschachteltes Bild/Textfragment einen
   eigenen nativen Browser-Drag starten; der Pointer-Fallback besitzt den Drag. */
.wc-pointer-drag-fallback .wc-block,
.wc-pointer-drag-fallback .wc-block *,
.wc-touch-interactions .wc-block,
.wc-touch-interactions .wc-block * {
  -webkit-user-drag: none;
}

/* Animationen deaktivieren für Geräte die das bevorzugen */
@media (prefers-reduced-motion: reduce) {
  .wc-block-upcoming,
  .wc-block-live {
    animation: none !important;
  }
}

.wc-appt-upcoming-badge {
  font-size: 0.6rem;
  font-weight: 700;
  color: #fff;
  background: rgba(0, 0, 0, 0.7);
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid rgba(255, 193, 7, 0.6);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
}

.wc-appt-live-badge {
  font-size: 0.55rem;
  font-weight: 800;
  color: #fff;
  background: rgba(76, 175, 80, 0.85);
  padding: 2px 5px;
  border-radius: 3px;
  letter-spacing: 0.5px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  animation: blink 2s infinite;
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* ===== GHOST ===== */
.ghost-block {
  position: absolute;
  left: 6px;
  right: 6px;
  z-index: 15;
  background: rgba(76, 175, 80, 0.05);
  border: 2px dashed rgba(76, 175, 80, 0.4);
  pointer-events: none;
  border-radius: 10px;
  will-change: transform;
}
.wc-resize-handle {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 50px;
  max-width: 80%;
  height: 12px;
  cursor: ns-resize;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  justify-content: center;
}
.wc-resize-handle.bottom {
  bottom: 0;
  align-items: flex-end;
  padding-bottom: 3px;
}
.wc-resize-handle.top {
  top: 0;
  align-items: flex-start;
  padding-top: 3px;
}
.wc-block:hover .wc-resize-handle {
  opacity: 1;
}
.wc-resize-handle::after {
  content: "";
  width: 24px;
  height: 4px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 2px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}
.error-ghost {
  background: rgba(244, 67, 54, 0.12);
  border-color: rgba(244, 67, 54, 0.4);
}
.wc-ghost-error {
  font-size: 0.65rem;
  font-weight: 600;
  color: #ef5350;
  background: rgba(0, 0, 0, 0.6);
  padding: 2px 8px;
  border-radius: 4px;
}

/* ===== CLICK GRID ===== */
.click-grid-layer {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  cursor: crosshair;
}
@media (any-hover: none) {
  .wc-block,
  .wc-block *,
  .click-grid-layer {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;
  }
}
.cursor-pointer {
  cursor: pointer;
}
.hover-opacity {
  transition: opacity 0.15s ease;
}
@media (any-hover: hover) {
  .hover-opacity:hover {
    opacity: 0.75;
  }
}
/* ===== EDITING PRESENCE INDICATOR ===== */
.wc-editing-indicator {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 20;
  pointer-events: none;
  border-radius: 10px;
  overflow: hidden;
}
.wc-editing-pulse {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 10px;
  border: 2px solid rgba(255, 193, 7, 0.6);
  animation: editPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes editPulse {
  0%,
  100% {
    border-color: rgba(255, 193, 7, 0.6);
    opacity: 1;
  }
  50% {
    border-color: rgba(255, 193, 7, 0.25);
    opacity: 0.7;
  }
}
.wc-editing-badge {
  position: absolute;
  top: -1px;
  right: -1px;
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px 2px 5px;
  background: linear-gradient(135deg, #ffa000, #ff8f00);
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  border-radius: 0 10px 0 8px;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.4);
  animation: editBadgeIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes editBadgeIn {
  0% {
    opacity: 0;
    transform: scale(0.5) translateX(10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateX(0);
  }
}
.editing-fade-enter-active {
  transition: opacity 0.3s ease;
}
.editing-fade-leave-active {
  transition: opacity 0.2s ease;
}
.editing-fade-enter,
.editing-fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Global styles for Vuetify dropdown menu overlays that mount to the body */
.wc-trainer-hover-card-menu {
  z-index: 99999 !important; /* Force menu overlay to be above calendar scroller stacking contexts */
}

.wc-trainer-hover-card {
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
}

.theme--light .wc-trainer-hover-card {
  background: #ffffff !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
}

.theme--dark .wc-trainer-hover-card {
  background: #1e1e1e !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* Appointment Hover Preview Card styles */
.wc-appt-preview-menu {
  z-index: 999999 !important;
  box-shadow: none !important;
}

.wc-appt-preview-card {
  border-radius: 12px !important;
  width: 600px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15) !important;
  border: 1px solid var(--border-medium) !important;
  transition: transform 0.25s ease;
  overflow: hidden;
}

.wc-appt-preview-card.wc-preview-compact {
  width: 280px !important;
}

.theme--light .wc-appt-preview-card {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
}

.theme--dark .wc-appt-preview-card {
  background: #1e1e1e !important;
  border: 1px solid #2d2d2d !important;
}

.wc-preview-header {
  background: rgba(0, 0, 0, 0.02);
}

.wc-preview-time {
  font-size: 0.78rem;
  color: var(--text-muted);
  font-weight: 500;
  display: flex;
  align-items: center;
}

.wc-preview-notes {
  background: rgba(var(--rgb-color), 0.02) !important;
  border: 1px solid rgba(var(--rgb-color), 0.06) !important;
}

.border-top-light {
  border-top: 1px solid var(--border-light);
}

/* Premium iOS context menu look */
.wc-preview-actions {
  display: flex;
  flex-direction: column;
}
.wc-preview-action-row {
  border-top: 1px solid var(--border-light);
  transition: background-color 0.15s ease;
  user-select: none;
}
.theme--light .wc-preview-action-row:hover {
  background-color: #f8f9fa;
}
.theme--dark .wc-preview-action-row:hover {
  background-color: #2a2a2a;
}
.wc-preview-action-row:last-child {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
}
.wc-preview-action-row .v-icon {
  color: var(--text-main) !important;
  opacity: 0.8;
}

/* Wallet Card Styles */
.wc-mini-membership-card {
  position: relative;
  border-radius: 10px !important;
  padding: 12px !important;
  box-sizing: border-box;
  font-family: inherit;
  transition: all 0.25s ease;
}

/* Premium Glassmorphic widgets */
.wc-preview-contact-panel {
  background: rgba(var(--rgb-color), 0.03);
  border: 1px solid rgba(var(--rgb-color), 0.06);
}
.wc-preview-flow {
  background: rgba(var(--rgb-color), 0.03) !important;
  border: 1px solid rgba(var(--rgb-color), 0.06) !important;
}

/* Shimmer Loading Animations */
.wc-shimmer {
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.12) 37%, rgba(255, 255, 255, 0.05) 63%);
  background-size: 400% 100%;
  animation: wc-shimmer-loading 1.4s ease infinite;
}
.theme--light .wc-shimmer {
  background: linear-gradient(90deg, rgba(0, 0, 0, 0.03) 25%, rgba(0, 0, 0, 0.08) 37%, rgba(0, 0, 0, 0.03) 63%);
  background-size: 400% 100%;
}
@keyframes wc-shimmer-loading {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
.wc-line-shimmer {
  border-radius: 4px;
}
.wc-card-shimmer {
  width: 100%;
}

/* Visits timeline list style */
.wc-visit-pill {
  font-family: monospace;
}
.gap-1\.5 {
  gap: 6px;
}

/* Glow shadow dynamic hover */
@media (any-hover: hover) {
  .wc-block:not(.item-pause):hover {
    z-index: 90 !important;
    opacity: 1 !important;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15), 0 0 10px var(--hover-shadow-color) !important;
  }
}

/* Mobile block text readability and description hiding */
@media (max-width: 960px) {
  .wc-block-title {
    font-size: 0.8rem !important;
  }
  .wc-block-subtitle {
    font-size: 0.7rem !important;
  }
  .wc-block-description {
    display: none !important;
  }
}
</style>
